import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";

import "./tasks.css";
import { AnimatePresence, LayoutGroup, MotionConfig, delay, motion } from "framer-motion";
import {
  DndContext,
  useDroppable,
  DragOverlay,
  pointerWithin,
  PointerSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";


import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { defaultAnimateLayoutChanges } from "@dnd-kit/sortable";
import { useHotkeys } from "react-hotkeys-hook";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../contexts/ThemeContext";

import { ReactComponent as Close } from "../icons/close.svg";
import { ReactComponent as Sun } from "../icons/sun.svg";
import { ReactComponent as Moon } from "../icons/moon.svg";
import { ReactComponent as Sunrise } from "../icons/sunrise.svg";
import { ReactComponent as Dots } from "../icons/dots.svg";
import { ReactComponent as Drag } from "../icons/drag.svg";
import { ReactComponent as Check } from "../icons/check.svg";
import { ReactComponent as CheckMark } from "../icons/check-mark.svg";
import { ReactComponent as Edit } from "../icons/edit.svg";
import { ReactComponent as Duplicate } from "../icons/duplicate.svg";
import { ReactComponent as Delete } from "../icons/delete.svg";
import { ReactComponent as Calendar } from "../icons/calendar.svg";
import { ReactComponent as Clock } from "../icons/clock.svg";
import { ReactComponent as ArrowDown } from "../icons/arrow-down.svg";
import { ReactComponent as ArrowLeft } from "../icons/arrow-left.svg"
import { ReactComponent as ArrowRight } from "../icons/arrow-right.svg"
import { ReactComponent as Undo } from "../icons/undo.svg";
import { ReactComponent as SunTheme } from "../icons/sun-theme.svg";
import { ReactComponent as MoonTheme } from "../icons/moon-theme.svg";
import { ReactComponent as SystemTheme } from "../icons/system-theme.svg";
import { ReactComponent as ImportTasks } from "../icons/import-tasks.svg";
import { ReactComponent as DownloadTasks } from "../icons/download-tasks.svg";
import { ReactComponent as About } from "../icons/about.svg";
import { ReactComponent as PrivacyPolicy } from "../icons/terms-of-use.svg";
import { ReactComponent as Twitter } from "../icons/twitter.svg";
import { ReactComponent as Changelog } from "../icons/changelog.svg";
import { ReactComponent as Logo } from "../icons/logo.svg";
import { ReactComponent as Mic } from "../icons/mic.svg";
import { ReactComponent as Settings } from "../icons/settings.svg";
import { ReactComponent as Search } from "../icons/search.svg";
import { ReactComponent as DeleteQuery } from "../icons/delete-query.svg";



import { usePageTitle } from "../hooks/usePageTitle";

import Fuse from "fuse.js";

import confetti from "canvas-confetti";

import Selecto from "react-selecto";

import dayjs from "dayjs";
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  addDays, addMonths, subMonths, isSameMonth, isSameDay, format
} from "date-fns"



const gridV = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { delayChildren: 0.03, staggerChildren: 0.012 } }
};
const cellV = {
  hidden: { opacity: 0, y: 6, filter: "blur(4px)" },
  show: { opacity: 1, y: 0, filter: "blur(0px)", transition: { type: "spring", stiffness: 520, damping: 34 } }
};


const CLOSE_ALL_EVENT = "task-dots-close-all";

const EASE_SOFT = [0.25, 0.8, 0.3, 1];
const POP = { type: "spring", stiffness: 420, damping: 42, mass: 0.7, delay: 0.03 };

const menuShell = {
  open: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      ...POP,
      duration: 0.38,
      when: "beforeChildren",
      delayChildren: 0.08,
      staggerChildren: 0.06
    }
  },
  closed: {
    opacity: 0,
    y: -10,
    scale: 0.97,
    transition: {
      duration: 0.28,
      ease: EASE_SOFT,
      when: "afterChildren",
      staggerDirection: -1
    }
  }
};

const itemsWrap = {
  open: { transition: { staggerChildren: 0.06 } },
  closed: { transition: { staggerChildren: 0.045, staggerDirection: -1 } }
};

const menuItem = {
  open: {
    opacity: 1,
    y: 0,
    x: 0,
    scale: 1,
    filter: "blur(0px)",
    transition: { type: "spring", stiffness: 460, damping: 40, mass: 0.68 }
  },
  closed: {
    opacity: 0,
    y: -6,
    x: 4,
    scale: 0.98,
    filter: "blur(4px)",
    transition: { duration: 0.14, ease: EASE_SOFT }
  }
};



const SortableTaskItem = React.memo(function SortableTaskItem({
  task,
  onCheck,
  onDelete,
  onDuplicate,
  onTitleCommit,
  onDateChange,
  onTimeChange,
  isSelected = false,
  isOverlay = false,
  isDeleting = false,
  isBaseHidden = false,
  isDraggingGlobal = false,
  draggedCount = 1,
  isGroupDragging = false,
  ...props
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: task.id,
    animateLayoutChanges: (args) => {
      const { isSorting, wasDragging } = args;
      if (isSorting || wasDragging) return false;
      return defaultAnimateLayoutChanges(args);
    },
  });

  const titleRef = useRef(null);

  const commitTitle = useCallback(() => {
    if (!onTitleCommit || !titleRef.current) return;
    const raw = titleRef.current.innerText;
    const next = raw.replace(/\s+/g, " ").trim();
    if (!next) {
      titleRef.current.innerText = task.title;
      return;
    }
    if (next !== task.title) onTitleCommit(next);
    else titleRef.current.innerText = task.title;
  }, [onTitleCommit, task.title]);

  const [dateCalOpen, setDateCalOpen] = React.useState(false);
  const [dateMonth, setDateMonth] = React.useState(() =>
    task?.due?.parsedDate ? new Date(task.due.parsedDate) : new Date()
  );
  const dateAnchorRef = React.useRef(null);
  const datePopRef = React.useRef(null);

  const selectedTaskDate = task?.due?.parsedDate
    ? new Date(task.due.parsedDate)
    : null;

  const [timeOpen, setTimeOpen] = React.useState(false);
  const timeAnchorRef = React.useRef(null);
  const timePopRef = React.useRef(null);

  const taskDueDate = task?.due?.parsedDate ? new Date(task.due.parsedDate) : null;
  const selectedHour = taskDueDate?.getHours();
  const selectedMinute = taskDueDate?.getMinutes();

  const nowTime = new Date();
  const isTodayTask =
    taskDueDate &&
    nowTime.toDateString() === taskDueDate.toDateString();

  useHotkeys(
    'esc',
    (e) => {
      let closed = false;
      if (dateCalOpen) {
        setDateCalOpen(false);
        closed = true;
      }
      if (timeOpen) {
        setTimeOpen(false);
        closed = true;
      }
      if (!closed) return;
      e.preventDefault();
    },
    { enableOnTags: ['*'], keydown: true }
  );

  React.useEffect(() => {
    if (!dateCalOpen) return;

    const base = task?.due?.parsedDate
      ? new Date(task.due.parsedDate)
      : new Date();

    setDateMonth(base);

    const handleOutside = (e) => {
      if (
        !datePopRef.current?.contains(e.target) &&
        !dateAnchorRef.current?.contains(e.target)
      ) {
        setDateCalOpen(false);
      }
    };

    document.addEventListener("pointerdown", handleOutside);
    return () => document.removeEventListener("pointerdown", handleOutside);
  }, [dateCalOpen, task?.due?.parsedDate]);

  React.useEffect(() => {
    if (!timeOpen) return;

    const handleOutside = (e) => {
      if (
        !timePopRef.current?.contains(e.target) &&
        !timeAnchorRef.current?.contains(e.target)
      ) {
        setTimeOpen(false);
      }
    };

    document.addEventListener("pointerdown", handleOutside);
    return () => document.removeEventListener("pointerdown", handleOutside);
  }, [timeOpen]);

  React.useEffect(() => {
    if (!timeOpen) return;
    const container = timePopRef.current;
    if (!container) return;

    const base = taskDueDate || new Date();
    const minutes = base.getHours() * 60 + base.getMinutes();
    const idx = Math.round(minutes / 30);

    const options = container.querySelectorAll(".time-option");
    const targetEl = options[idx];
    if (!targetEl) return;

    setTimeout(() => {
      const targetOffset =
        targetEl.offsetTop - container.clientHeight / 2 + targetEl.clientHeight / 2;
      const start = container.scrollTop;
      const diff = targetOffset - start;
      const duration = 620;
      const startTime = performance.now();

      const animate = (nowTime) => {
        const t = Math.min((nowTime - startTime) / duration, 1);
        const easeOutCubic = 1 - Math.pow(1 - t, 3);
        container.scrollTop = start + diff * easeOutCubic;
        if (t < 1) requestAnimationFrame(animate);
      };

      requestAnimationFrame(animate);
    }, 120);
  }, [timeOpen, taskDueDate]);

  const [justChecked, setJustChecked] = React.useState(false);
  useEffect(() => { if (!task.completed) setJustChecked(false); }, [task.completed]);


  const nowForPopup = new Date();
  const dueForPopup = task?.due?.parsedDate ? new Date(task.due.parsedDate) : null;

  let daysLeftText = "";
  let popupTone = "";

  if (dueForPopup) {
    const todayMid = new Date(
      nowForPopup.getFullYear(),
      nowForPopup.getMonth(),
      nowForPopup.getDate()
    );
    const dueMid = new Date(
      dueForPopup.getFullYear(),
      dueForPopup.getMonth(),
      dueForPopup.getDate()
    );

    const d = Math.round((dueMid - todayMid) / (1000 * 60 * 60 * 24));
    const diffMs = dueForPopup.getTime() - nowForPopup.getTime();
    const sign = diffMs >= 0 ? 1 : -1;
    const totalMinutes = Math.max(0, Math.round(Math.abs(diffMs) / 60000));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const suffix = sign >= 0 ? "left" : "late";

    if (d > 1) {
      daysLeftText = `${d} days left`;
    } else if (d === 1) {
      daysLeftText = "1 day left";
    } else if (d === 0) {
      if (totalMinutes === 0) {
        daysLeftText = sign >= 0 ? "Due now" : "Just late";
      } else if (hours === 0) {
        daysLeftText = `${totalMinutes}m ${suffix}`;
      } else {
        const mm = minutes.toString().padStart(2, "0");
        daysLeftText =
          minutes > 0
            ? `${hours}h ${mm}m ${suffix}`
            : `${hours}h ${suffix}`;
      }
    } else {
      const a = Math.abs(d);
      daysLeftText = `${a} day${a === 1 ? "" : "s"} late`;
    }

    if (props.dateClass === "task-date-error") {
      popupTone = "late";
    } else if (props.dateClass === "task-date-warning") {
      popupTone = "near";
    } else if (props.dateClass === "task-date-accent") {
      popupTone = "accent";
    } else if (props.dateClass === "task-date-completed") {
      popupTone = "completed";
    }
  }


  const disableLayout = isDragging || isOverlay || isDraggingGlobal || isBaseHidden;

  const menuRef = useRef(null);
  const [menuOpen, setMenuOpen] = React.useState(false);

  React.useEffect(() => {
    const handler = (e) => {
      if (e.detail !== task.id) setMenuOpen(false);
    };
    window.addEventListener(CLOSE_ALL_EVENT, handler);
    return () => window.removeEventListener(CLOSE_ALL_EVENT, handler);
  }, [task.id]);

  useEffect(() => {
    if (!menuOpen) return;
    const onDocPointerDown = (e) => {
      if (!menuRef.current) return;
      if (!menuRef.current?.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("pointerdown", onDocPointerDown);
    return () => document.removeEventListener("pointerdown", onDocPointerDown);
  }, [menuOpen]);

  let finalTransform = transform;

  if (
    !isOverlay &&
    !isBaseHidden &&
    finalTransform &&
    isDraggingGlobal &&
    draggedCount > 1 &&
    !isGroupDragging
  ) {
    finalTransform = {
      ...finalTransform,
      y: finalTransform.y * draggedCount,
    };
  }

  const dndStyle =
    !isOverlay && !isBaseHidden && finalTransform
      ? { transform: CSS.Transform.toString(finalTransform), transition }
      : {};

  return (
    <motion.div
      ref={setNodeRef}
      data-id={task.id}
      className={
        `task-item selectable ${isSelected ? "selected " : ""}${isOverlay ? "task-item-dragged" : ""}` +
        (isDeleting ? " deleting" : "")
      }
      layout={!disableLayout}
      layoutId={!disableLayout ? task.id : undefined}
      transition={
        !disableLayout
          ? { type: "spring", stiffness: 700, damping: 50 }
          : { duration: 0 }
      }
      animate={isDeleting ? { opacity: 0, y: -8, scale: 0.985 } : undefined}
      exit={{ opacity: 0, y: -8, scale: 0.985, transition: { duration: 0.18 } }}
      style={{
        ...dndStyle,
        opacity: isOverlay ? 1 : (isBaseHidden ? 0 : 1),
        pointerEvents: isDeleting ? "none" : undefined,
        willChange: !isOverlay && isDragging ? "transform" : "auto",
      }}
      {...props}
    >
      <div className="task-item-left">
        <span {...listeners} {...attributes} style={{ cursor: "grab" }}>
          <Drag className="drag-handle-icon" />
        </span>

        <motion.div
          className={`check-square${task.completed || justChecked ? " checked" : ""}`}
          data-id={task.id}
          onClick={() => { if (!task.completed) setJustChecked(true); onCheck?.(); }}
          whileTap={{ scale: 0.92 }}
          animate={task.completed ? { scale: [1, 1.06, 1] } : {}}
          transition={{ duration: 3.2 }}
        >
          {(task.completed || justChecked) && <Check className="check-icon" />}
        </motion.div>

        <p
          ref={titleRef}
          className="task-title"
          tabIndex={-1}
          contentEditable={!isOverlay}
          suppressContentEditableWarning
          onBlur={commitTitle}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commitTitle();
              e.currentTarget.blur();
            }
            if (e.key === "Escape") {
              e.preventDefault();
              if (titleRef.current) titleRef.current.innerText = task.title;
              e.currentTarget.blur();
            }
          }}
        >
          {task.title}
        </p>

      </div>

      <div className="task-item-right" style={{ position: "relative" }}>
        <div
          className="task-time-wrapper"
          ref={timeAnchorRef}
          onClick={(e) => {
            e.stopPropagation();
            if (isOverlay) return;
            setTimeout(() => {
              setTimeOpen((s) => !s);
            }, 40);
          }}
          style={{ position: "relative" }}
        >
          <span className={`task-time ${props.timeClass}`}>{props.time}</span>

          <AnimatePresence>
            {timeOpen && !isOverlay && (
              <motion.div
                key="task-time-popup"
                ref={timePopRef}
                className="time-picker-dropdown"
                initial={{ opacity: 0, y: 8, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 8, scale: 0.96 }}
                transition={{ duration: 0.25, ease: EASE_SOFT }}
                style={{
                  position: "absolute",
                  top: (timeAnchorRef.current?.offsetTop || 0) + 33,
                  right: 0,
                  zIndex: 3050,
                }}
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
              >
                {Array.from({ length: 48 }, (_, i) => {
                  const totalMinutes = i * 30;
                  const hour24 = Math.floor(totalMinutes / 60);
                  const minute = totalMinutes % 60;

                  const label = new Date(0, 0, 0, hour24, minute).toLocaleTimeString([], {
                    hour: "numeric",
                    minute: "2-digit",
                    hour12: true,
                  });

                  const isSelected =
                    taskDueDate &&
                    selectedHour === hour24 &&
                    selectedMinute === minute;

                  let isPastForToday = false;
                  if (isTodayTask) {
                    const candidate = new Date(nowTime);
                    candidate.setHours(hour24, minute, 0, 0);
                    isPastForToday = candidate.getTime() < nowTime.getTime();
                  }

                  return (
                    <div
                      key={`${hour24}-${minute}`}
                      className={
                        `time-option` +
                        (isSelected ? " selected" : "") +
                        (isPastForToday ? " past" : "")
                      }
                      onClick={() => {
                        if (isPastForToday) return;

                        const base = taskDueDate || new Date();
                        const picked = new Date(base);
                        picked.setHours(hour24, minute, 0, 0);

                        setTimeOpen(false);
                        setTimeout(() => {
                          onTimeChange?.(picked);
                        }, 90);
                      }}
                    >
                      <span>{label}</span>
                      {isSelected && <CheckMark className="time-check" />}
                    </div>
                  );
                })}

              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div
          className={`task-date-wrapper${dateCalOpen ? " calendar-open" : ""}`}
          ref={dateAnchorRef}
          onClick={(e) => {
            e.stopPropagation();
            if (isOverlay) return;
            setTimeout(() => {
              setDateCalOpen(s => !s);
            }, 40);
          }}
        >
          <motion.span className={`task-date ${props.dateClass}`}>
            {props.date}
          </motion.span>

          {dueForPopup && !task.completed && (
            <motion.div
              className={`task-date-popup ${popupTone}`}
              role="tooltip"
              aria-hidden={isOverlay ? "true" : "false"}
            >
              {daysLeftText}
            </motion.div>
          )}

          <AnimatePresence>
            {dateCalOpen && !isOverlay && (
              <motion.div
                key="task-calendar-popup"
                ref={datePopRef}
                initial={{ opacity: 0, y: 8, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 8, scale: 0.95 }}
                transition={{ duration: 0.3, ease: [0.25, 0.8, 0.3, 1] }}
                className="calendar-popup"
                style={{
                  position: "absolute",
                  top: (dateAnchorRef.current?.offsetTop || 0) + 38,
                  right: 0,
                  zIndex: 3000,
                  transformOrigin: "top right",
                  willChange: "transform, opacity",
                }}
                onClick={e => e.stopPropagation()}
                layout
              >
                {(() => {
                  const monthStart = startOfMonth(dateMonth);
                  const monthEnd = endOfMonth(dateMonth);
                  const startDate = startOfWeek(monthStart);
                  const endDate = endOfWeek(monthEnd);

                  const rows = [];
                  let days = [];
                  let day = startDate;

                  while (day <= endDate) {
                    for (let i = 0; i < 7; i++) {
                      const clone = day;
                      const isPast = day < new Date().setHours(0, 0, 0, 0);
                      const isSelected =
                        selectedTaskDate && isSameDay(day, selectedTaskDate);

                      days.push(
                        <motion.button
                          key={day.getTime()}
                          variants={cellV}
                          whileHover={!isPast ? { scale: 1.04 } : {}}
                          whileTap={!isPast ? { scale: 0.96 } : {}}
                          disabled={isPast}
                          className={
                            "calendar-day" +
                            (!isSameMonth(day, dateMonth) ? " dim" : "") +
                            (isPast ? " disabled" : "") +
                            (isSelected ? " selected" : "")
                          }
                          onClick={() => {
                            if (isPast) return;
                            setDateCalOpen(false);
                            if (onDateChange) {
                              setTimeout(() => {
                                onDateChange(clone);
                              }, 100);
                            }
                          }}
                        >
                          {format(day, "d")}
                        </motion.button>
                      );
                      day = addDays(day, 1);
                    }
                    rows.push(
                      <div className="calendar-row" key={day.getTime()}>
                        {days}
                      </div>
                    );
                    days = [];
                  }

                  return (
                    <div className="calendar-box">
                      <div className="calendar-head">
                        <button
                          type="button"
                          className="calendar-arrow-container"
                          onClick={e => {
                            e.stopPropagation();
                            setDateMonth(m => subMonths(m, 1));
                          }}
                        >
                          <ArrowLeft className="calendar-arrow" />
                        </button>

                        <span>{format(dateMonth, "MMMM yyyy")}</span>

                        <button
                          type="button"
                          className="calendar-arrow-container"
                          onClick={e => {
                            e.stopPropagation();
                            setDateMonth(m => addMonths(m, 1));
                          }}
                        >
                          <ArrowRight className="calendar-arrow" />
                        </button>
                      </div>

                      <motion.div
                        key={dateMonth.getTime()}
                        variants={gridV}
                        initial="hidden"
                        animate="show"
                        className="calendar-body"
                      >
                        {rows}
                      </motion.div>
                    </div>
                  );
                })()}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div
          className="task-dots-container"
          ref={menuRef}
          onPointerDownCapture={(e) => { e.stopPropagation(); }}
          onPointerDown={(e) => e.stopPropagation()}
          onTouchStartCapture={(e) => { e.stopPropagation(); }}
          onTouchStart={(e) => e.stopPropagation()}
          onClick={() => {
            setTimeout(() => {
              setMenuOpen((prev) => {
                const next = !prev;
                if (next) {
                  window.dispatchEvent(new CustomEvent(CLOSE_ALL_EVENT, { detail: task.id }));
                }
                return next;
              });
            }, 35);
          }}
        >
          <span className="task-dots" role="button" tabIndex={0} aria-label="Task actions">
            <Dots />
          </span>

          <AnimatePresence initial={false} mode="wait">
            {menuOpen && (
              <motion.div
                key="menu"
                className="task-dots-dropdown"
                role="menu"
                variants={menuShell}
                initial="closed"
                animate="open"
                exit="closed"
                layout
                style={{ transformOrigin: "top right" }}
                onClick={(e) => e.stopPropagation()}
              >
                <motion.div variants={itemsWrap} initial="closed" animate="open" exit="closed">
                  <motion.button
                    className="dots-option"
                    role="menuitem"
                    variants={menuItem}
                    whileHover={{ x: 1.5, scale: 1.004 }}
                    whileTap={{ scale: 0.992 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onDuplicate?.(task.id);
                      setMenuOpen(false);
                    }}
                  >
                    <Duplicate className="dots-option-icon" /> Duplicate
                  </motion.button>

                  <motion.button
                    className="dots-option delete"
                    role="menuitem"
                    variants={menuItem}
                    whileHover={{ x: 1.5, scale: 1.004 }}
                    whileTap={{ scale: 0.992 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!onDelete) return;
                      setTimeout(() => {
                        onDelete(task.id);
                        setMenuOpen(false);
                      }, 50);
                    }}
                  >
                    <Delete className="dots-option-icon delete" /> Delete
                  </motion.button>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
});










const Tasks = () => {
  usePageTitle("Foxer - Tasks");
  const nav = useNavigate();


  const handleTitleCommit = (id, title, fromCompleted = false) => {
    const v = title.trim();
    if (!v) return;
    const nowIso = new Date().toISOString();

    if (fromCompleted) {
      setCompletedTasks(prev =>
        prev.map(t => t.id === id ? { ...t, title: v, updatedAt: nowIso } : t)
      );
    } else {
      setTasks(prev =>
        prev.map(t => t.id === id ? { ...t, title: v, updatedAt: nowIso } : t)
      );
    }
  };

  const handleTaskTimeChange = (id, fromCompleted, pickedDate) => {
    const mergeTime = (t) => {
      const base = t?.due?.parsedDate ? new Date(t.due.parsedDate) : new Date();
      const d = new Date(base);
      d.setHours(
        pickedDate.getHours(),
        pickedDate.getMinutes(),
        0,
        0
      );
      return {
        ...t,
        due: { ...t.due, parsedDate: d.toISOString() },
        updatedAt: new Date().toISOString(),
      };
    };

    if (fromCompleted) {
      setCompletedTasks(prev =>
        prev.map(t => (t.id === id ? mergeTime(t) : t))
      );
    } else {
      setTasks(prev =>
        prev.map(t => (t.id === id ? mergeTime(t) : t))
      );
    }
  };



  const DroppableContainer = React.memo(({ id, children }) => {
    const { setNodeRef } = useDroppable({ id });

    return (
      <div
        ref={setNodeRef}
        style={{ minHeight: 50, width: "100%", display: "flex", flexDirection: "column" }}
      >
        {children}
      </div>
    );
  });


  const searchInputRef = useRef(null);
  const keepSearchFocusRef = useRef(false);



  React.useLayoutEffect(() => {
    if (!keepSearchFocusRef.current) return;
    const el = searchInputRef.current;
    if (!el) return;

    if (document.activeElement !== el) el.focus({ preventScroll: true });

    const len = el.value?.length ?? 0;
    try { el.setSelectionRange(len, len); } catch { }
  });

  useEffect(() => {
    const onPointerDown = (e) => {
      const el = searchInputRef.current;
      if (!el) return;

      const inside = e.target === el || el.contains(e.target);
      keepSearchFocusRef.current = inside;

      if (inside) {
        requestAnimationFrame(() => el.focus({ preventScroll: true }));
      }
    };

    document.addEventListener("pointerdown", onPointerDown, true);
    return () => document.removeEventListener("pointerdown", onPointerDown, true);
  }, []);





  const [isInputFocused, setIsInputFocused] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const isInteractingWithControls = useRef(false);

  const fileInputRef = useRef(null);

  const [pendingImport, setPendingImport] = useState(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);

  const [importToast, setImportToast] = useState(null);


  const [voiceState, setVoiceState] = useState("idle"); // idle | recording | processing | error
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const volumeLoopRef = useRef(null);
  const voiceBarsRef = useRef(null);
  const maxVoiceLevelRef = useRef(0);


  const [isOnline, setIsOnline] = useState(() => navigator.onLine);

  useEffect(() => {
    const updateOnline = () => setIsOnline(navigator.onLine);
    window.addEventListener("online", updateOnline);
    window.addEventListener("offline", updateOnline);
    return () => {
      window.removeEventListener("online", updateOnline);
      window.removeEventListener("offline", updateOnline);
    };
  }, []);



  useEffect(() => {
    if (!isInputFocused) return;

    const onDocPointerDown = (e) => {
      if (inputRef.current?.contains(e.target)) return;
      if (containerRef.current?.contains(e.target)) return;

      setTimeout(() => setIsInputFocused(false), 0);
    };

    document.addEventListener("pointerdown", onDocPointerDown);
    return () => document.removeEventListener("pointerdown", onDocPointerDown);
  }, [isInputFocused]);



  useEffect(() => {
    if (!isInputFocused) {
      setTimePickerOpen(false);
      setCalendarOpen(false);
    }
  }, [isInputFocused]);

  const [timePickerOpen, setTimePickerOpen] = useState(false);
  const timePickerAnchorRef = useRef(null);
  const [selectedTime, setSelectedTime] = useState(null);

  const [selectedDate, setSelectedDate] = useState(() => new Date());


  useEffect(() => {
    if (!timePickerOpen) return;
    const onDocDown = (e) => {
      if (!timePickerAnchorRef.current) return;
      const root = timePickerAnchorRef.current;
      if (!root.contains(e.target) && !(document.querySelector('.mui-timepicker-popper')?.contains(e.target))) {
        setTimePickerOpen(false);
      }
    };
    document.addEventListener("pointerdown", onDocDown);
    return () => document.removeEventListener("pointerdown", onDocDown);
  }, [timePickerOpen]);

  const vibrateLight = () => {
    if (navigator.vibrate) navigator.vibrate(12);
  };



  const addTask = (title) => {
    const nowIso = new Date().toISOString();
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    const now = dayjs();
    let parsedDateIso = nowIso;

    if (selectedDate) {
      let d = dayjs(selectedDate);

      if (selectedTime) {
        d = d
          .hour(selectedTime.hour())
          .minute(selectedTime.minute())
          .second(0)
          .millisecond(0);
      } else {
        const future = now.add(1, "hour");
        d = d
          .hour(future.hour())
          .minute(future.minute())
          .second(0)
          .millisecond(0);
      }

      parsedDateIso = d.toISOString();
    } else if (selectedTime) {
      const combined = now
        .hour(selectedTime.hour())
        .minute(selectedTime.minute())
        .second(0)
        .millisecond(0);
      parsedDateIso = combined.toISOString();
    } else {
      const future = now.add(1, "hour").second(0).millisecond(0);
      parsedDateIso = future.toISOString();
    }

    const newTask = {
      id,
      title,
      createdAt: nowIso,
      updatedAt: nowIso,
      due: {
        originalInput: selectedTime ? selectedTime.format("HH:mm") : "",
        parsedDate: parsedDateIso,
      },
      completed: false,
      focused: false,
      deleted: false,
    };

    setTasks((prev) => [newTask, ...prev]);

    vibrateLight();

    setSelectedDate(new Date());
    setCalendarMonth(new Date());

    if (inputRef.current) inputRef.current.value = "";
    setSelectedTime(null);
    setTimePickerOpen(false);
  };



  const duplicateTask = (id, fromCompleted = false) => {
    if (fromCompleted) {
      const idx = completedTasks.findIndex((t) => t.id === id);
      if (idx === -1) return;
      const src = completedTasks[idx];
      const nowIso = new Date().toISOString();
      const newTask = {
        ...src,
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        createdAt: nowIso,
        updatedAt: nowIso,
      };
      setCompletedTasks((prev) => {
        const before = prev.slice(0, idx + 1);
        const after = prev.slice(idx + 1);
        return [...before, newTask, ...after];
      });
    } else {
      const idx = tasks.findIndex((t) => t.id === id);
      if (idx === -1) return;
      const src = tasks[idx];
      const nowIso = new Date().toISOString();
      const newTask = {
        ...src,
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        createdAt: nowIso,
        updatedAt: nowIso,
      };
      setTasks((prev) => {
        const before = prev.slice(0, idx + 1);
        const after = prev.slice(idx + 1);
        return [...before, newTask, ...after];
      });
    }
  };

  const daysFromNow = (n, hours = 12) => {
    const d = new Date(Date.now() + n * 864e5);
    d.setUTCHours(hours, 0, 0, 0);
    return d.toISOString();
  };

  const nowDefault = new Date();

  const defaultTasks = [
    {
      id: "1",
      title: "Mark this task complete",
      createdAt: nowDefault.toISOString(),
      updatedAt: nowDefault.toISOString(),
      due: {
        originalInput: "today 6pm",
        parsedDate: daysFromNow(0, 18),
      },
      completed: false,
      focused: true,
      deleted: false,
    },
    {
      id: "2",
      title: "Reorder tasks by dragging",
      createdAt: nowDefault.toISOString(),
      updatedAt: nowDefault.toISOString(),
      due: {
        originalInput: "tomorrow 7am",
        parsedDate: daysFromNow(1, 7),
      },
      completed: false,
      focused: false,
      deleted: false,
    },
    {
      id: "3",
      title: "Edit a task title by clicking on it",
      createdAt: nowDefault.toISOString(),
      updatedAt: nowDefault.toISOString(),
      due: {
        originalInput: "Saturday 4pm",
        parsedDate: daysFromNow(2, 16),
      },
      completed: false,
      focused: false,
      deleted: false,
    },
    {
      id: "4",
      title: "Open the settings menu",
      createdAt: nowDefault.toISOString(),
      updatedAt: nowDefault.toISOString(),
      due: {
        originalInput: "Monday morning",
        parsedDate: daysFromNow(4, 9),
      },
      completed: false,
      focused: false,
      deleted: false,
    },
  ];

  const defaultCompletedTasks = [
    {
      id: "5",
      title: "Drag and drop this in active tasks",
      createdAt: nowDefault.toISOString(),
      updatedAt: nowDefault.toISOString(),
      due: {
        originalInput: "today 6pm",
        parsedDate: daysFromNow(3, 16),
      },
      completed: true,
      focused: true,
      deleted: false,
    },
  ];


  const [tasks, setTasks] = useState(() => {
    const savedTasks = localStorage.getItem("tasks");
    return savedTasks ? JSON.parse(savedTasks) : defaultTasks;
  });


  const [completedTasks, setCompletedTasks] = useState(() => {
    const savedTasks = localStorage.getItem("completedTasks");
    return savedTasks ? JSON.parse(savedTasks) : defaultCompletedTasks;
  });

  useEffect(() => {
    if (!importToast) return;
    const t = setTimeout(() => setImportToast(null), 3600);
    return () => clearTimeout(t);
  }, [importToast]);

  const applyImport = useCallback(
    (mode) => {
      if (!pendingImport) return;
      const { tasks: importedTasks, completedTasks: importedCompleted } = pendingImport;

      if (mode === "replace") {
        setTasks(importedTasks);
        setCompletedTasks(importedCompleted);
        setImportToast({ type: "success", message: "Tasks imported and replaced current." });
      } else if (mode === "merge") {
        const ids = new Set([
          ...tasks.map((t) => t.id),
          ...completedTasks.map((t) => t.id),
        ]);

        const ensureUniqueId = (t) => {
          let id = t.id;
          while (ids.has(id)) {
            id = `${id}-${Math.random().toString(36).slice(2, 6)}`;
          }
          ids.add(id);
          return { ...t, id };
        };

        const mergedTasks = [
          ...tasks,
          ...importedTasks.map(ensureUniqueId),
        ];
        const mergedCompleted = [
          ...completedTasks,
          ...importedCompleted.map(ensureUniqueId),
        ];

        setTasks(mergedTasks);
        setCompletedTasks(mergedCompleted);
        setImportToast({ type: "success", message: "Tasks imported and merged." });
      }

      setPendingImport(null);
      setImportDialogOpen(false);
    },
    [pendingImport, tasks, completedTasks]
  );

  const cancelImport = useCallback(() => {
    setPendingImport(null);
    setImportDialogOpen(false);
  }, []);


  const exportAllTasks = useCallback(() => {
    const payload = {
      version: EXPORT_VERSION,
      exportedAt: new Date().toISOString(),
      tasks,
      completedTasks,
    };

    const blob = new Blob([JSON.stringify(payload, null, 2)], {
      type: "application/json",
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");

    a.href = url;
    a.download = `foxer-tasks-${new Date().toISOString().slice(0, 10)}.json`;

    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);

    setImportToast({
      type: "success",
      message: "Tasks downloaded as backup.",
    });
  }, [tasks, completedTasks]);



  const EXPORT_VERSION = 1;

  const handleImportFile = useCallback(
    (e) => {
      const file = e.target.files?.[0];
      if (!file) return;

      const reader = new FileReader();

      reader.onload = (evt) => {
        try {
          const text = evt.target?.result;
          const json = JSON.parse(text);

          if (!json || typeof json !== "object") {
            throw new Error("Invalid JSON");
          }

          const {
            version,
            tasks: importedTasks,
            completedTasks: importedCompleted,
          } = json;

          if (
            version !== EXPORT_VERSION ||
            !Array.isArray(importedTasks) ||
            !Array.isArray(importedCompleted)
          ) {
            throw new Error("Not a valid Foxer export file");
          }

          const nowIso = new Date().toISOString();

          const normalize = (arr, completedFlag) =>
            arr
              .filter((t) => t && typeof t === "object" && t.title)
              .map((t) => ({
                id:
                  typeof t.id === "string" && t.id.trim()
                    ? t.id
                    : `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                title: String(t.title),
                createdAt: t.createdAt || nowIso,
                updatedAt: nowIso,
                completed:
                  typeof t.completed === "boolean" ? t.completed : completedFlag,
                deleted: false,
                due:
                  t.due && t.due.parsedDate
                    ? {
                      originalInput: t.due.originalInput || "",
                      parsedDate: t.due.parsedDate,
                    }
                    : {
                      originalInput: "",
                      parsedDate: nowIso,
                    },
              }));

          const normalizedTasks = normalize(importedTasks, false);
          const normalizedCompleted = normalize(importedCompleted, true);

          setPendingImport({
            tasks: normalizedTasks,
            completedTasks: normalizedCompleted,
          });
          setImportDialogOpen(true);
        } catch (err) {
          console.error(err);
          setImportToast({
            type: "error",
            message:
              "Could not import tasks. Make sure this is a valid Foxer export file.",
          });
        } finally {
          e.target.value = "";
        }
      };

      reader.readAsText(file);
    },
    []
  );




  useEffect(() => {
    localStorage.setItem("tasks", JSON.stringify(tasks));
  }, [tasks]);

  useEffect(() => {
    localStorage.setItem("completedTasks", JSON.stringify(completedTasks));
  }, [completedTasks]);



  const getHour = (d = new Date()) => d.getHours();

  const getGreeting = (h = getHour()) =>
    h >= 6 && h < 12
      ? "Good morning"
      : h >= 12 && h < 18
        ? "Good afternoon"
        : h >= 18 && h < 22
          ? "Good evening"
          : "Good night";

  const getIcon = (h = getHour()) =>
    h >= 6 && h < 12
      ? <Sunrise className="title-icon" />
      : h >= 12 && h < 18
        ? <Sun className="title-icon" />
        : <Moon className="title-icon" />;


  const formatTaskDate = (dateString) => {
    if (!dateString) return { time: "", date: "" };
    const date = new Date(dateString);
    const time = date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });

    const today = new Date();
    const todayMid = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const dateMid = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const diffDays = Math.round((dateMid - todayMid) / (1000 * 60 * 60 * 24));

    let dateLabel;
    if (diffDays === 0) {
      dateLabel = "Today";
    } else if (diffDays === 1) {
      dateLabel = "Tomorrow";
    } else {
      const day = date.getDate();
      const month = date.toLocaleString("en-US", { month: "short" });
      dateLabel = `${day} ${month}`;
    }

    return { time, date: dateLabel };
  };

  const launchSmallConfetti = (element) => {
    if (!element) return;
    const rect = element.getBoundingClientRect();
    confetti({
      particleCount: 20,
      spread: 20,
      startVelocity: 9,
      ticks: 90,
      origin: {
        x: (rect.left + rect.width / 2) / window.innerWidth,
        y: (rect.top + rect.height / 2 - 20) / window.innerHeight,
      },
      scalar: 0.4,
      zIndex: 9999,
      disableForReducedMotion: true,
    });
  };

  const getTaskDateClass = (task) => {
    if (task.completed) return "task-date-completed";

    const now = new Date();
    const dueDate = new Date(task.due.parsedDate);
    const diffMs = dueDate - now;
    const diffDays = diffMs / (1000 * 60 * 60 * 24);

    if (diffDays < 0) {
      return "task-date-error";
    } else if (diffDays < 1) {
      return "task-date-warning";
    } else {
      return "task-date-accent";
    }
  };

  const getTaskTimeClass = (task) => {
    if (task.completed) return "task-time-completed";
    const now = new Date();
    const dueDate = new Date(task.due.parsedDate);
    const diffMs = dueDate - now;
    const diffHours = diffMs / (1000 * 60 * 60);

    if (diffHours < 3) {
      return "task-time-warning";
    } else {
      return;
    }
  };

  const [isCompletedTasksOpen, setIsCompletedTasksOpen] = useState(true);

  const toggleCompletedTasks = () => {
    setIsCompletedTasksOpen(!isCompletedTasksOpen);
  };

  const [activeId, setActiveId] = useState(null);

  const [isDragging, setIsDragging] = useState(false);

  const [deletingIds, setDeletingIds] = useState([]);
  const DELETE_ANIM_MS = 250;

  const [multiDragging, setMultiDragging] = useState([]);
  const [multiOffsets, setMultiOffsets] = useState({});

  const multiPointerOffsetYRef = useRef(0);

  const deleteTaskWithAnimation = useCallback((id, fromCompleted = false) => {
    setDeletingIds((s) => [...s, id]);
    setTimeout(() => {
      if (fromCompleted) {
        setCompletedTasks((prev) => prev.filter((t) => t.id !== id));
      } else {
        setTasks((prev) => prev.filter((t) => t.id !== id));
      }
      setDeletingIds((s) => s.filter((x) => x !== id));
    }, DELETE_ANIM_MS);
  }, []);

  function handleDragStart(event) {
    const activeId = event.active.id;
    setActiveId(activeId);
    setIsDragging(true);

    let group = selectedTaskIds.slice();

    if (!group.length || !group.includes(activeId)) group = [activeId];

    group = group.filter(id => document.querySelector(`[data-id="${id}"]`));

    const rects = {};
    group.forEach(id => {
      const el = document.querySelector(`[data-id="${id}"]`);
      if (!el) return;
      rects[id] = el.getBoundingClientRect();
    });

    const baseRect = rects[activeId] || Object.values(rects)[0];
    if (!baseRect) {
      setMultiDragging([]);
      setMultiOffsets({});
      multiPointerOffsetYRef.current = 0;
      return;
    }

    const tops = Object.values(rects).map(r => r.top);
    const bottoms = Object.values(rects).map(r => r.bottom);
    const groupTop = Math.min(...tops);
    const groupBottom = Math.max(...bottoms);
    const groupCenter = (groupTop + groupBottom) / 2;
    const activeCenter = (baseRect.top + baseRect.bottom) / 2;

    multiPointerOffsetYRef.current = groupCenter - activeCenter;

    const offsets = {};
    Object.entries(rects).forEach(([id, r]) => {
      offsets[id] = {
        x: r.left - baseRect.left,
        y: r.top - baseRect.top,
      };
    });

    if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    setHiddenIds(group);

    setMultiDragging(group);
    setMultiOffsets(offsets);
  }



  const reorderBlockByStart = (ids, groupIds, desiredStart) => {
    const N = ids.length;
    const groupSet = new Set(groupIds);
    const group = ids.filter(id => groupSet.has(id));
    const g = group.length;
    if (!g) return ids;

    let start = desiredStart;
    if (start < 0) start = 0;
    if (start > N - g) start = N - g;

    const others = ids.filter(id => !groupSet.has(id));
    const res = [];
    let gi = 0;
    let oi = 0;

    for (let pos = 0; pos < N; pos++) {
      if (pos >= start && pos < start + g) {
        res.push(group[gi++]);
      } else {
        res.push(others[oi++]);
      }
    }
    return res;
  };


  const reorderMultiTasks = (list, rawGroupIds, activeId, overId) => {
    const ids = list.map(t => t.id);
    if (!ids.includes(activeId) || !ids.includes(overId)) return list;

    let groupIds = rawGroupIds.filter(id => ids.includes(id));
    if (!groupIds.length) groupIds = [activeId];

    const groupSet = new Set(groupIds);
    if (groupSet.has(overId)) return list;

    const N = ids.length;
    const idxs = ids
      .map((id, i) => (groupSet.has(id) ? i : -1))
      .filter(i => i !== -1);

    const first = Math.min(...idxs);
    const last = Math.max(...idxs);
    const overIndex = ids.indexOf(overId);
    const g = groupIds.length;

    let desiredStart;
    if (g === 1) {
      desiredStart = overIndex;
    } else {
      if (overIndex > last) {
        desiredStart = overIndex - g + 1;
      } else if (overIndex < first) {
        desiredStart = overIndex;
      } else {
        desiredStart = overIndex;
      }
    }

    const newIds = reorderBlockByStart(ids, groupIds, desiredStart);
    const idToTask = new Map(list.map(t => [t.id, t]));
    return newIds.map(id => idToTask.get(id));
  };

  const resetDragState = () => {
    setActiveId(null);
    setIsDragging(false);
    setMultiDragging([]);
    setMultiOffsets({});
    multiPointerOffsetYRef.current = 0;
  };

  const [hiddenIds, setHiddenIds] = useState([]);
  const hideTimeoutRef = useRef(null);

  const DROP_MS = 260;


  function handleDragEnd(event) {
    const { active, over } = event;

    if (!over) {
      const idsToHide = multiDragging.length ? multiDragging : [active.id];

      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
      setHiddenIds(idsToHide);

      hideTimeoutRef.current = setTimeout(() => {
        setHiddenIds([]);
        hideTimeoutRef.current = null;
        resetDragState();
      }, DROP_MS);

      return;
    }

    const activeId = active.id;
    const overId = over.id;
    const groupIds = multiDragging.length ? multiDragging : [activeId];

    const isActiveInTasks = tasks.some(t => t.id === activeId);
    const isOverInTasks = tasks.some(t => t.id === overId);
    const isActiveInCompleted = completedTasks.some(t => t.id === activeId);
    const isOverInCompleted = completedTasks.some(t => t.id === overId);

    if (isActiveInTasks && isOverInTasks) {
      setTasks(prev => reorderMultiTasks(prev, groupIds, activeId, overId));
    } else if (isActiveInCompleted && isOverInCompleted) {
      setCompletedTasks(prev => reorderMultiTasks(prev, groupIds, activeId, overId));
    } else if (isActiveInTasks && isOverInCompleted) {
      const sourceIds = new Set(
        groupIds.filter(id => tasks.some(t => t.id === id))
      );
      if (sourceIds.size) {
        const moved = tasks
          .filter(t => sourceIds.has(t.id))
          .map(t => ({ ...t, completed: true }));
        const remaining = tasks.filter(t => !sourceIds.has(t.id));
        setTasks(remaining);
        setCompletedTasks(prev => [...moved, ...prev]);
        setIsCompletedTasksOpen(true);
      }
    } else if (isActiveInCompleted && isOverInTasks) {
      const sourceIds = new Set(
        groupIds.filter(id => completedTasks.some(t => t.id === id))
      );
      if (sourceIds.size) {
        const moved = completedTasks
          .filter(t => sourceIds.has(t.id))
          .map(t => ({ ...t, completed: false }));
        const remaining = completedTasks.filter(t => !sourceIds.has(t.id));
        setCompletedTasks(remaining);
        setTasks(prev => [...prev, ...moved]);
      }
    }

    if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    setHiddenIds(groupIds);

    hideTimeoutRef.current = setTimeout(() => {
      setHiddenIds([]);
      hideTimeoutRef.current = null;
    }, DROP_MS);

    resetDragState();
  }








  const vibrateSoft = () => navigator.vibrate?.(14);

  const handleCheck = (task, fromCompleted) => {

    vibrateSoft();

    if (!fromCompleted) {
      setIsCompletedTasksOpen(true);
      const el = document.querySelector(`.check-square[data-id="${task.id}"]`);
      if (el) { el.classList.add("checked"); launchSmallConfetti(el); }

      setTimeout(() => {
        setTasks(prev => prev.filter(t => t.id !== task.id));
        setCompletedTasks(prev => [{ ...task, completed: true }, ...prev]);
        setIsCompletedTasksOpen(true);
      }, 550);
    } else {
      setTimeout(() => {
        setCompletedTasks(prev => prev.filter(t => t.id !== task.id));
        setTasks(prev => [...prev, { ...task, completed: false }]);
      }, 80);

    }
  };


  useEffect(() => {
    const styleId = "selecto-disable-pointer";
    let styleTag = document.getElementById(styleId);

    const root = document.querySelector(".tasks-container");

    if (isDragging) {
      if (!styleTag) {
        styleTag = document.createElement("style");
        styleTag.id = styleId;
        styleTag.innerHTML = `
          .selectable, .selecto-selection {
            pointer-events: none !important;
          }
        `;
        document.head.appendChild(styleTag);
      }
      if (root && !root.classList.contains("task-holding")) {
        root.classList.add("task-holding");
      }
    } else {
      if (styleTag) {
        styleTag.remove();
      }
      if (root && root.classList.contains("task-holding")) {
        root.classList.remove("task-holding");
      }
    }
    return () => {
      if (styleTag) styleTag.remove();
      if (root && root.classList.contains("task-holding")) {
        root.classList.remove("task-holding");
      }
    };
  }, [isDragging]);

  const selectoRef = useRef(null);

  const [selectedTaskIds, setSelectedTaskIds] = useState([]);

  const clearSelection = useCallback(() => {
    try {
      selectoRef.current?.setSelectedTargets?.([]);
    } catch { }
    setSelectedTaskIds([]);
  }, []);

  const selectedIdSet = useMemo(() => new Set(selectedTaskIds), [selectedTaskIds]);

  useEffect(() => {
    if (!selectedTaskIds.length) return;
    const existing = new Set([
      ...tasks.map((t) => t.id),
      ...completedTasks.map((t) => t.id),
    ]);

    const next = selectedTaskIds.filter((id) => existing.has(id));
    if (next.length === selectedTaskIds.length) return;
    setSelectedTaskIds(next);
  }, [selectedTaskIds, tasks, completedTasks]);

  const bulkMarkDone = useCallback(() => {
    if (!selectedTaskIds.length) return;
    const idSet = new Set(selectedTaskIds);
    const moved = tasks
      .filter((t) => idSet.has(t.id))
      .map((t) => ({ ...t, completed: true, updatedAt: new Date().toISOString() }));

    if (moved.length) {
      setTasks((prev) => prev.filter((t) => !idSet.has(t.id)));
      setCompletedTasks((prev) => [...moved, ...prev]);
      setIsCompletedTasksOpen(true);
    }
    clearSelection();
  }, [selectedTaskIds, tasks, clearSelection]);

  const bulkMarkUndone = useCallback(() => {
    if (!selectedTaskIds.length) return;
    const idSet = new Set(selectedTaskIds);
    const moved = completedTasks
      .filter((t) => idSet.has(t.id))
      .map((t) => ({ ...t, completed: false, updatedAt: new Date().toISOString() }));

    if (moved.length) {
      setCompletedTasks((prev) => prev.filter((t) => !idSet.has(t.id)));
      setTasks((prev) => [...prev, ...moved]);
    }
    clearSelection();
  }, [selectedTaskIds, completedTasks, clearSelection]);

  const bulkDelete = useCallback(() => {
    if (!selectedTaskIds.length) return;
    const activeIds = new Set(tasks.map((t) => t.id));
    const completedIds = new Set(completedTasks.map((t) => t.id));
    selectedTaskIds.forEach((id) => {
      if (activeIds.has(id)) deleteTaskWithAnimation(id, false);
      else if (completedIds.has(id)) deleteTaskWithAnimation(id, true);
    });
    clearSelection();
  }, [selectedTaskIds, tasks, completedTasks, deleteTaskWithAnimation, clearSelection]);

  const selectedActiveCount = useMemo(() => {
    if (!selectedTaskIds.length) return 0;
    const activeSet = new Set(tasks.map((t) => t.id));
    return selectedTaskIds.reduce((n, id) => n + (activeSet.has(id) ? 1 : 0), 0);
  }, [selectedTaskIds, tasks]);

  const selectedCompletedCount = useMemo(() => {
    if (!selectedTaskIds.length) return 0;
    const completedSet = new Set(completedTasks.map((t) => t.id));
    return selectedTaskIds.reduce((n, id) => n + (completedSet.has(id) ? 1 : 0), 0);
  }, [selectedTaskIds, completedTasks]);

  useHotkeys('a', (e) => {
    e.preventDefault();
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  });

  useHotkeys(
    'esc',
    () => {
      const noPopups = !calendarOpen && !timePickerOpen && !headerMenuOpen
      if (timePickerOpen) setTimePickerOpen(false)
      if (calendarOpen) setCalendarOpen(false)
      if (headerMenuOpen) setHeaderMenuOpen(false)
      if (selectedTaskIds.length) clearSelection()
      if (noPopups) {
        setIsInputFocused(false)
        inputRef.current?.blur()
      }
      window.dispatchEvent(new CustomEvent(CLOSE_ALL_EVENT, { detail: null }))
    },
    { enableOnTags: ['*'], keydown: true }
  )

  useHotkeys(
    'esc',
    (e) => {
      if (importDialogOpen) {
        e.preventDefault()
        cancelImport()
        return
      }

    },
    { enableOnTags: ['*'], keydown: true }
  )

  useHotkeys(
    'd',
    (e) => {
      if (!selectedTaskIds.length || isTyping()) return;
      if (selectedActiveCount === 0) return;
      e.preventDefault();
      bulkMarkDone();
    },
    { enableOnTags: ['*'], keydown: true }
  );

  useHotkeys(
    'g',
    (e) => {
      if (!selectedTaskIds.length || isTyping()) return;
      if (selectedCompletedCount === 0) return;
      e.preventDefault();
      bulkMarkUndone();
    },
    { enableOnTags: ['*'], keydown: true }
  );

  useHotkeys(
    'x',
    (e) => {
      if (!selectedTaskIds.length || isTyping()) return;
      e.preventDefault();
      bulkDelete();
    },
    { enableOnTags: ['*'], keydown: true }
  );

  const todaysCount = tasks.filter(
    (task) =>
      new Date(task.due.parsedDate).toDateString() ===
      new Date().toDateString() && !task.completed
  ).length;



  const currentMoodRef = React.useRef(null);
  const activeBufRef = React.useRef('a');
  const clearTimerRef = React.useRef(null);
  const FADE_MS = 420;

  const moodColorMap = {
    warning: 'var(--mood-yellow)',
    error: 'var(--mood-red)',
    success: 'var(--mood-green)',
  };

  const setBgMood = (mood) => {
    if (clearTimerRef.current) {
      clearTimeout(clearTimerRef.current);
      clearTimerRef.current = null;
    }

    const layer = document.getElementById('mood-layer');
    if (!layer) return;

    const bufA = layer.querySelector('.mood-a');
    const bufB = layer.querySelector('.mood-b');
    const active = activeBufRef.current === 'a' ? bufA : bufB;
    const idle = activeBufRef.current === 'a' ? bufB : bufA;

    if (!mood) {
      active.style.opacity = '0';
      currentMoodRef.current = null;
      return;
    }

    if (currentMoodRef.current === mood) return;


    idle.style.setProperty('--mood-color', moodColorMap[mood] || 'transparent');
    idle.style.opacity = '1';
    active.style.opacity = '0';


    activeBufRef.current = activeBufRef.current === 'a' ? 'b' : 'a';
    currentMoodRef.current = mood;
  };

  const moodFromDateClass = (cls, task) => {
    if (!cls || task?.completed) return null;
    if (cls.includes('task-date-error')) return 'error';
    if (cls.includes('task-date-warning')) return 'warning';
    if (cls.includes('task-date-accent')) return 'success';
    return null;
  };



  useEffect(() => {
    if (document.getElementById('mood-layer')) return;

    const layer = document.createElement('div');
    layer.id = 'mood-layer';

    const a = document.createElement('div');
    a.className = 'mood mood-a';

    const b = document.createElement('div');
    b.className = 'mood mood-b';

    layer.appendChild(a);
    layer.appendChild(b);
    document.body.appendChild(layer);

    return () => {
      layer.remove();
    };
  }, []);


  const now = new Date();
  const month = now.toLocaleString('en-US', { month: 'short' });
  const day = now.getDate();
  const dateLabel = `${day} ${month}`;

  const isInputDateToday =
    selectedDate &&
    selectedDate.toDateString() === now.toDateString();



  const completedRef = useRef(null);
  const [showScrollBtn, setShowScrollBtn] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      const completedEl = completedRef.current;
      if (!completedEl) return;
      const rect = completedEl.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      setShowScrollBtn(rect.top > viewportHeight * 0.9);
    };
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const [calendarMonth, setCalendarMonth] = useState(new Date())
  const [calendarOpen, setCalendarOpen] = useState(false)
  const calendarAnchorRef = useRef(null)
  const calendarPopRef = useRef(null)

  useEffect(() => {
    if (calendarOpen) setCalendarMonth(selectedDate || new Date());
    if (!calendarOpen) return
    const handleOutside = e => {
      if (!calendarPopRef.current?.contains(e.target) &&
        !calendarAnchorRef.current?.contains(e.target)) {
        setCalendarOpen(false)
      }
    }
    document.addEventListener("pointerdown", handleOutside)
    return () => document.removeEventListener("pointerdown", handleOutside)
  }, [calendarOpen, selectedDate])

  useEffect(() => {
    if (!timePickerOpen) return;

    const now = new Date();
    const idx = Math.round((now.getHours() * 60 + now.getMinutes()) / 30);
    const targetEl = document.querySelector(`.time-option:nth-child(${idx + 1})`);
    const container = document.querySelector(".time-picker-dropdown");
    if (!targetEl || !container) return;

    setTimeout(() => {
      const targetOffset =
        targetEl.offsetTop - container.clientHeight / 2 + targetEl.clientHeight / 2;
      const start = container.scrollTop;
      const diff = targetOffset - start;
      const duration = 620;
      const startTime = performance.now();

      const animate = (nowTime) => {
        const t = Math.min((nowTime - startTime) / duration, 1);
        const easeOutCubic = 1 - Math.pow(1 - t, 3);
        container.scrollTop = start + diff * easeOutCubic;
        if (t < 1) requestAnimationFrame(animate);
      };

      requestAnimationFrame(animate);
    }, 120);
  }, [timePickerOpen]);


  const [nlDate, setNlDate] = useState(null);
  const [nlLabel, setNlLabel] = useState("");

  const [manualTimeSet, setManualTimeSet] = useState(false);
  const [manualDateSet, setManualDateSet] = useState(false);

  const [headerMenuOpen, setHeaderMenuOpen] = useState(false);
  const headerMenuRef = useRef(null);

  const { theme, setTheme } = useTheme();

  useEffect(() => {
    if (!headerMenuOpen) return;

    const onDocPointerDown = (e) => {
      if (!headerMenuRef.current) return;
      if (!headerMenuRef.current.contains(e.target)) {
        setHeaderMenuOpen(false);
      }
    };

    document.addEventListener("pointerdown", onDocPointerDown);
    return () => document.removeEventListener("pointerdown", onDocPointerDown);
  }, [headerMenuOpen]);


  let chronoCache = null;

  const lazyParseDate = async (raw) => {
    if (!chronoCache) {
      chronoCache = await import("chrono-node");
    }
    return chronoCache.parse(raw);
  };

  const liveParseNatural = async (raw) => {
    const results = await lazyParseDate(raw);


    if (!results.length) {
      if (!manualDateSet) setNlDate(null);
      if (!manualTimeSet) setSelectedTime(null);
      setNlLabel("");
      return;
    }

    const r = results[0];
    const dateObj = r.start.date();

    if (!manualDateSet) {
      setSelectedDate(dateObj);
    }
    if (!manualTimeSet) {
      setSelectedTime(
        dayjs().hour(dateObj.getHours()).minute(dateObj.getMinutes())
      );
    }

    setNlDate(dateObj);
    setNlLabel(dayjs(dateObj).format("ddd, DD MMM · HH:mm"));
  };



  useEffect(() => {
    if (!inputRef.current) return;

    const updatePadding = () => {
      const right = document.querySelector(".task-input-right");
      if (!right) return;

      const w = right.getBoundingClientRect().width;
      inputRef.current.style.paddingRight = `${w + 20}px`;
    };

    updatePadding();

    const ro = new ResizeObserver(updatePadding);
    const right = document.querySelector(".task-input-right");
    if (right) ro.observe(right);

    return () => ro.disconnect();
  }, [isInputFocused, selectedTime, selectedDate, timePickerOpen, calendarOpen]);


  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: { distance: 6 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 1,
        tolerance: 8,
      },
    }),
  );

  const collisionDetection = useCallback(
    (args) => {
      const { active, pointerCoordinates } = args;

      if (
        !pointerCoordinates ||
        multiDragging.length <= 1 ||
        !multiDragging.includes(active.id) ||
        !multiPointerOffsetYRef.current
      ) {
        return pointerWithin(args);
      }

      const shiftedArgs = {
        ...args,
        pointerCoordinates: {
          x: pointerCoordinates.x,
          y: pointerCoordinates.y + multiPointerOffsetYRef.current,
        },
      };

      return pointerWithin(shiftedArgs);
    },
    [multiDragging]
  );

  const handleTaskDateChange = (id, fromCompleted, pickedDate) => {
    const mergeDate = t => {
      const base = t?.due?.parsedDate ? new Date(t.due.parsedDate) : new Date();
      const d = new Date(pickedDate);
      d.setHours(
        base.getHours(),
        base.getMinutes(),
        base.getSeconds(),
        base.getMilliseconds()
      );
      return {
        ...t,
        due: { ...t.due, parsedDate: d.toISOString() },
        updatedAt: new Date().toISOString(),
      };
    };

    if (fromCompleted) {
      setCompletedTasks(prev =>
        prev.map(t => (t.id === id ? mergeDate(t) : t))
      );
    } else {
      setTasks(prev =>
        prev.map(t => (t.id === id ? mergeDate(t) : t))
      );
    }
  };



  const BACKEND_URL =
    window.location.hostname === "localhost"
      ? "http://localhost:4000"
      : "https://foxer-2cxx.onrender.com";

  useEffect(() => {
    if (!isOnline) return;

    if (sessionStorage.getItem("sttWarmed")) return;
    sessionStorage.setItem("sttWarmed", "1");

    fetch(`${BACKEND_URL}/api/health`, { cache: "no-store" }).catch(() => { });
  }, [isOnline]);

  const blobToBase64 = (blob) =>
    new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(String(r.result).split(",")[1]);
      r.onerror = reject;
      r.readAsDataURL(blob);
    });


  const transcribeWithGroq = async blob => {
    try {
      const arrayBuffer = await blob.arrayBuffer();
      const uint8 = new Uint8Array(arrayBuffer);
      let binary = "";
      for (let i = 0; i < uint8.length; i++) binary += String.fromCharCode(uint8[i]);
      const base64 = await blobToBase64(blob);

      const res = await fetch(`${BACKEND_URL}/api/transcribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          audio: base64,
          mimeType: blob.type,
        }),
      });

      if (!res.ok) throw new Error(`Backend error ${res.status}`);
      const data = await res.json();
      return (data.text || "").trim();
    } catch (err) {
      console.error("Transcription failed:", err);
      setVoiceState("error");
      return "";
    }
  };



  const cleanupVoiceResources = () => {
    if (volumeLoopRef.current) {
      cancelAnimationFrame(volumeLoopRef.current);
      volumeLoopRef.current = null;
    }

    if (voiceBarsRef.current) {
      Array.from(voiceBarsRef.current.children).forEach(bar => {
        bar.style.transform = "scaleY(0.4)";
      });
    }

    if (audioCtxRef.current) {
      audioCtxRef.current.close();
      audioCtxRef.current = null;
    }
  };


  const stopRecording = () => {
    const rec = mediaRecorderRef.current;
    if (rec && rec.state !== "inactive") {
      rec.stop();
      setVoiceState("processing");
    }
  };

  const startRecording = async () => {
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        console.error("getUserMedia not supported");
        setVoiceState("error");
        return;
      }

      maxVoiceLevelRef.current = 0;
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // AudioContext + analyser for reactive waves
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const audioCtx = new AudioCtx();
      audioCtxRef.current = audioCtx;

      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 1024;
      source.connect(analyser);
      analyserRef.current = analyser;

      const dataArray = new Uint8Array(analyser.frequencyBinCount);

      const loop = () => {
        if (!analyserRef.current) return;
        analyserRef.current.getByteTimeDomainData(dataArray);

        let sum = 0;
        for (let i = 0; i < dataArray.length; i++) {
          const v = (dataArray[i] - 128) / 128;
          sum += v * v;
        }
        const rms = Math.sqrt(sum / dataArray.length);
        const level = Math.min(1, rms * 4);

        maxVoiceLevelRef.current = Math.max(maxVoiceLevelRef.current, level);

        const barsRoot = voiceBarsRef.current;
        if (barsRoot) {
          const bars = barsRoot.children;
          for (let i = 0; i < bars.length; i++) {
            const factor = 0.4 + level * (1.2 + i * 0.15);
            bars[i].style.transform = `scaleY(${factor})`;
          }
        }

        volumeLoopRef.current = requestAnimationFrame(loop);
      };
      loop();



      const rec = new MediaRecorder(stream);
      mediaRecorderRef.current = rec;
      audioChunksRef.current = [];

      rec.ondataavailable = e => {
        if (e.data?.size) audioChunksRef.current.push(e.data);
      };

      rec.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        cleanupVoiceResources();

        const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        audioChunksRef.current = [];

        const MIN_LEVEL = 0.08;  // threshold
        const MIN_SIZE = 2000;   // bytes

        if (maxVoiceLevelRef.current < MIN_LEVEL || blob.size < MIN_SIZE) {
          setVoiceState("idle");
          return;
        }

        setVoiceState("processing");
        const text = await transcribeWithGroq(blob);
        setVoiceState("idle");

        if (!text || !inputRef.current) return;

        const prev = inputRef.current.value || "";
        const next = prev ? `${prev.trimEnd()} ${text}` : text;
        inputRef.current.value = next;
        liveParseNatural(next);
        setIsInputFocused(true);
        inputRef.current.focus();
      };



      rec.start();
      setVoiceState("recording");
    } catch (err) {
      console.error("Mic start failed:", err);
      cleanupVoiceResources();
      setVoiceState("error");
    }
  };

  const handleVoiceClick = () => {
    if (voiceState === "recording") {
      stopRecording();
    } else if (voiceState === "idle" || voiceState === "error") {
      setIsInputFocused(true);
      inputRef.current?.focus();
      startRecording();
    }
  };

  const formatDate = () => {
    const d = new Date()
    return `${d.toLocaleString("en-US", { month: "short" })} ${d.getDate()} · ${d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`
  }

  const InfoTime = () => {
    const [time, setTime] = useState(formatDate())

    useEffect(() => {
      const update = () => setTime(formatDate())
      update()
      const id = setInterval(update, 60_000)

      return () => clearInterval(id)
    }, [])

    return time
  }

  const isVoiceDisabled = !isOnline;

  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");

  const startOfDay = (d) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

  const isToday = (iso) => {
    const d = new Date(iso);
    return startOfDay(d) === startOfDay(new Date());
  };

  const isOverdue = (iso) => new Date(iso).getTime() < Date.now();

  const isUpcoming = (iso) => {
    const d = new Date(iso);
    return startOfDay(d) > startOfDay(new Date());
  };


  const filteredTasks = React.useMemo(() => {
    if (activeFilter === "today") {
      return tasks.filter(t => !t.completed && isToday(t.due?.parsedDate));
    }

    if (activeFilter === "upcoming") {
      return tasks.filter(t => !t.completed && isUpcoming(t.due?.parsedDate));
    }

    if (activeFilter === "overdue") {
      return tasks.filter(t => !t.completed && isOverdue(t.due?.parsedDate));
    }

    return tasks.filter(t => !t.completed);
  }, [tasks, activeFilter]);


  const isTyping = () => {
    const el = document.activeElement;
    if (!el) return false;
    return (
      el.tagName === "INPUT" ||
      el.tagName === "TEXTAREA" ||
      el.isContentEditable
    );
  };

  useHotkeys("1", e => { if (isTyping()) return; e.preventDefault(); setActiveFilter("all"); });
  useHotkeys("2", e => { if (isTyping()) return; e.preventDefault(); setActiveFilter("today"); });
  useHotkeys("3", e => { if (isTyping()) return; e.preventDefault(); setActiveFilter("upcoming"); });
  useHotkeys("4", e => { if (isTyping()) return; e.preventDefault(); setActiveFilter("overdue"); });


  const q = searchQuery.trim().toLowerCase();

  const searchedTasks = useMemo(() => {
    if (!q) return filteredTasks;

    // 1-char: cheap includes match (or startsWith if you want stricter)
    if (q.length === 1) {
      return filteredTasks.filter(t => (t.title || "").toLowerCase().includes(q));
    }

    const fuse = new Fuse(filteredTasks, {
      keys: ["title"],
      threshold: 0.35,
      ignoreLocation: true,
      minMatchCharLength: 2,
    });

    return fuse.search(q).map(r => r.item);
  }, [filteredTasks, q]);

  const searchSet = useMemo(() => {
    if (!q) return null;

    if (q.length === 1) {
      return new Set(
        filteredTasks
          .filter(t => (t.title || "").toLowerCase().includes(q))
          .map(t => t.id)
      );
    }

    const fuse = new Fuse(filteredTasks, {
      keys: ["title"],
      threshold: 0.35,
      ignoreLocation: true,
      minMatchCharLength: 2,
    });

    return new Set(fuse.search(q).map(r => r.item.id));
  }, [filteredTasks, q]);


  const MULTI_SELECT_TIP_KEY = "foxer_multi_select_tip_v1";

  const showMultiSelectTip = useCallback((msg) => {
    if (localStorage.getItem(MULTI_SELECT_TIP_KEY)) return;
    if (importToast) return;
    setImportToast({ type: "success", message: msg });
    localStorage.setItem(MULTI_SELECT_TIP_KEY, "1");
  }, [importToast]);

  useEffect(() => {
    if (localStorage.getItem(MULTI_SELECT_TIP_KEY)) return;

    const t = setTimeout(() => {
      if (isDragging || calendarOpen || timePickerOpen || headerMenuOpen || importDialogOpen) return;
      showMultiSelectTip("Tip: Drag to multi-select tasks");
    }, 5400);

    return () => clearTimeout(t);
  }, [showMultiSelectTip, isDragging, calendarOpen, timePickerOpen, headerMenuOpen, importDialogOpen]);



  return (
    <motion.div className="tasks-container"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.6, delay: 0.5 }}
    >
      <div className="tasks-subcontainer">
        <div className="tasks-header">
          <div className="tasks-header-text">
            <motion.div
              initial={{ opacity: 0, y: 20, filter: "blur(6px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              exit={{ opacity: 0, y: 20, filter: "blur(6px)" }}
              transition={{ duration: 0.4, delay: 0.8 }}
              className="title-container"
            >
              {getIcon()}
              <p className="title">{getGreeting()}</p>
            </motion.div>

            <motion.p
              initial={{ opacity: 0, y: 10, filter: "blur(6px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              exit={{ opacity: 0, y: 10, filter: "blur(6px)" }}
              transition={{ duration: 0.4, delay: 1.2 }}
              className="information"
            >
              You have {tasks.length} remaining tasks,{" "}
              <span className={todaysCount > 0 ? "today-count active" : "today-count"}>
                {todaysCount} today
              </span>
              .
            </motion.p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 10, filter: "blur(6px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            exit={{ opacity: 0, y: 10, filter: "blur(6px)" }}
            transition={{ duration: 0.4, delay: 0.3 }}
            className="header-dots-container"
            ref={headerMenuRef}
            onPointerDownCapture={(e) => { e.stopPropagation(); }}
            onPointerDown={(e) => e.stopPropagation()}
            onTouchStartCapture={(e) => { e.stopPropagation(); }}
            onTouchStart={(e) => e.stopPropagation()}
            onClick={() => {
              setTimeout(() => {
                setHeaderMenuOpen((prev) => !prev);
              }, 35);
            }}
          >


            <span className="header-dots" role="button" tabIndex={0} aria-label="Header actions">
              <Settings />
            </span>

            <AnimatePresence initial={false} mode="wait">
              {headerMenuOpen && (
                <motion.div
                  key="menu"
                  className="header-dots-dropdown"
                  role="menu"
                  variants={menuShell}
                  initial="closed"
                  animate="open"
                  exit="closed"
                  layout
                  style={{ transformOrigin: "top right" }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <motion.div variants={itemsWrap} initial="closed" animate="open" exit="closed">
                    <div className="dropdown-section-title theme">Theme</div>

                    <div className="theme-options-row">
                      <motion.button
                        className="theme-option"
                        role="menuitem"
                        variants={menuItem}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => setTheme("system")}
                      >
                        {theme === "system" && (
                          <motion.div
                            className="theme-selection-bg"
                            layoutId="theme-selection"
                            transition={{ type: "spring", bounce: 0.25, duration: 0.6 }}
                          />
                        )}
                        <span className={`theme-option-content${theme === "system" ? " selected" : ""}`}>
                          <SystemTheme className="theme-option-icon" /> System
                        </span>
                      </motion.button>

                      <motion.button
                        className="theme-option"
                        role="menuitem"
                        variants={menuItem}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => setTheme("dark")}
                      >
                        {theme === "dark" && (
                          <motion.div
                            className="theme-selection-bg"
                            layoutId="theme-selection"
                            transition={{ type: "spring", bounce: 0.25, duration: 0.6 }}
                          />
                        )}
                        <span className={`theme-option-content${theme === "dark" ? " selected" : ""}`}>
                          <MoonTheme className="theme-option-icon" /> Dark
                        </span>
                      </motion.button>

                      <motion.button
                        className="theme-option"
                        role="menuitem"
                        variants={menuItem}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => setTheme("light")}
                      >
                        {theme === "light" && (
                          <motion.div
                            className="theme-selection-bg"
                            layoutId="theme-selection"
                            transition={{ type: "spring", bounce: 0.25, duration: 0.6 }}
                          />
                        )}
                        <span className={`theme-option-content${theme === "light" ? " selected" : ""}`}>
                          <SunTheme className="theme-option-icon" /> Light
                        </span>
                      </motion.button>
                    </div>

                    <div className="dropdown-section-title">Tasks</div>

                    <motion.button
                      className="dots-option header tasks"
                      role="menuitem"
                      variants={menuItem}
                      whileHover={{ x: 1.5, scale: 1.004 }}
                      whileTap={{ scale: 0.992 }}
                      onClick={exportAllTasks}
                    >
                      <DownloadTasks className="dots-option-icon header" /> Download tasks
                    </motion.button>

                    <motion.button
                      className="dots-option header tasks"
                      role="menuitem"
                      variants={menuItem}
                      whileHover={{ x: 1.5, scale: 1.004 }}
                      whileTap={{ scale: 0.992 }}
                      onClick={() => { fileInputRef.current?.click(); setTimeout(() => setHeaderMenuOpen(false), 160) }}
                    >
                      <ImportTasks className="dots-option-icon header" /> Import tasks
                    </motion.button>


                    <div className="dropdown-divider"></div>

                    <motion.button
                      className="dots-option header disabled"
                      role="menuitem"
                      variants={menuItem}
                      whileHover={{ x: 1.5, scale: 1.004 }}
                      whileTap={{ scale: 0.992 }}
                    >
                      <Changelog className="dots-option-icon header disabled" /> Changelog
                    </motion.button>

                    <motion.button
                      className="dots-option header"
                      role="menuitem"
                      variants={menuItem}
                      whileHover={{ x: 1.5, scale: 1.004 }}
                      whileTap={{ scale: 0.992 }}
                      onClick={() => nav("/privacy-policy")}
                    >
                      <PrivacyPolicy className="dots-option-icon header" /> Privacy policy
                    </motion.button>

                    <motion.button
                      className="dots-option header"
                      role="menuitem"
                      variants={menuItem}
                      whileHover={{ x: 1.5, scale: 1.004 }}
                      whileTap={{ scale: 0.992 }}
                      onClick={() => nav("/about")}
                    >
                      <About className="dots-option-icon header" /> About
                    </motion.button>

                    <motion.button
                      className="dots-option header twitter"
                      role="menuitem"
                      variants={menuItem}
                      whileHover={{ x: 1.5, scale: 1.004 }}
                      whileTap={{ scale: 0.992 }}
                      onClick={() => window.open('https://x.com/FoxerHQ', '_blank')}
                    >
                      <Twitter className="dots-option-icon header twitter" /> Follow us on X
                    </motion.button>

                  </motion.div>
                  <div className="dropdown-divider"></div>
                  <motion.p
                    role="menuitem"
                    variants={menuItem}
                    className="header-version"
                  >© 2025 Foxer · v2.0 beta</motion.p>
                </motion.div>
              )}
            </AnimatePresence>


          </motion.div>
        </div>
        <motion.div
          className="task-input-container"
          ref={containerRef}
        >
          <AnimatePresence initial={false}>
            {isInputFocused && (
              <motion.div
                key="left"
                className="task-input-left"
                initial={{ opacity: 0, x: -6 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -6 }}
                transition={{ duration: 0.22, ease: [0.25, 0.8, 0.3, 1] }}
                aria-hidden
              >
                <span className="drag-handle-icon lite"><Drag /></span>
                <div className="check-square lite" />
              </motion.div>
            )}
          </AnimatePresence>

          <motion.input
            ref={inputRef}
            placeholder="Create a new task..."
            className="task-add-input"
            onFocus={() => setIsInputFocused(true)}
            onBlur={() => {
              setTimeout(() => {
                if (isInteractingWithControls.current) return;
                setIsInputFocused(false);
              }, 100);
            }}
            animate={isInputFocused ? "focused" : "idle"}
            transition={{ duration: 0.22, ease: [0.25, 0.8, 0.3, 1] }}
            onChange={(e) => {
              const v = e.target.value;
              if (!v.trim()) {
                setManualTimeSet(false);
                setManualDateSet(false);
              }
              liveParseNatural(v);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                e.stopPropagation();

                const val = e.currentTarget.value.trim();
                if (val) {
                  addTask(val);
                  setTimePickerOpen(false);
                  setCalendarOpen(false);
                }
              }

              if (e.key === "Escape") {
                const noPopups = !calendarOpen && !timePickerOpen;
                if (noPopups) {
                  e.currentTarget.blur();
                  setIsInputFocused(false);
                }

                if (timePickerOpen) setTimePickerOpen(false);
                if (calendarOpen) setCalendarOpen(false);
              }
            }}
          />



          <div
            className="task-input-right"
            onMouseDown={(e) => {
              e.preventDefault();
              isInteractingWithControls.current = true;
            }}
            onTouchStart={(e) => {
              e.preventDefault();
              isInteractingWithControls.current = true;
            }}
            onPointerDown={(e) => {
              e.preventDefault();
              isInteractingWithControls.current = true;
              setTimeout(() => { isInteractingWithControls.current = false; }, 500);
            }}
          >

            <AnimatePresence initial={false} mode="wait">
              {!isInputFocused ? (
                <motion.div
                  key="kbd"
                  className="task-add-tooltip"
                  initial={{ opacity: 0, y: 6, scale: 0.96 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -6, scale: 0.96 }}
                  transition={{ duration: 0.18, ease: [0.25, 0.8, 0.3, 1] }}
                >
                  A
                </motion.div>

              ) : (
                <div className="task-center">

                  <motion.button
                    type="button"
                    key="voice"
                    layout
                    className={`task-date-chip voice-chip voice-${voiceState}${isVoiceDisabled ? " unavailable" : ""}`}
                    initial={{ opacity: 0, y: 6, x: 4 }}
                    animate={{ opacity: 1, y: 0, x: 0 }}
                    exit={{ opacity: 0, y: -6, x: 4 }}
                    transition={{ type: "spring", stiffness: 520, damping: 36, mass: 0.7 }}
                    onClick={e => {
                      e.stopPropagation();
                      if (isVoiceDisabled) return;
                      handleVoiceClick();
                    }}
                    onPointerDownCapture={e => e.stopPropagation()}
                    aria-pressed={voiceState === "recording"}
                    aria-label={isVoiceDisabled ? "Voice input unavailable offline" : "Add task by voice"}
                    disabled={isVoiceDisabled}
                  >
                    <motion.div
                      className="clock-ico-container"
                      initial={{ opacity: 0, x: 8, scale: 0.96, filter: "blur(2px)" }}
                      animate={{ opacity: 1, x: 0, scale: 1, filter: "blur(0px)" }}
                      exit={{ opacity: 0, x: 8, scale: 0.96, filter: "blur(2px)" }}
                      transition={{ delay: 0.15, duration: 0.2, ease: [0.25, 0.8, 0.3, 1] }}
                    >
                      <Mic className={`chip-ico mic-ico ${isVoiceDisabled ? "unavailable" : ""}`} />

                    </motion.div>


                    {voiceState === "recording" && (
                      <div className="voice-bars" aria-hidden="true" ref={voiceBarsRef}>
                        {[0, 1, 2, 3, 4].map(i => (
                          <span key={i} className="voice-bar" />
                        ))}
                      </div>
                    )}


                    {voiceState === "processing" && (
                      <div className="voice-spinner" aria-hidden="true" />
                    )}
                  </motion.button>


                  <motion.div
                    layout
                    key="clock"
                    className={`task-date-chip task-clock-chip ${selectedTime ? "expanded" : ""}`}
                    initial={{ opacity: 0, y: 6, x: 4 }}
                    animate={{ opacity: 1, y: 0, x: 0 }}
                    exit={{ opacity: 0, y: -6, x: 4 }}
                    transition={{ type: "spring", stiffness: 520, damping: 36, mass: 0.7 }}
                    ref={timePickerAnchorRef}
                    onPointerDownCapture={(e) => { e.stopPropagation(); }}
                    onClick={e => {
                      e.stopPropagation()
                      if (calendarOpen) setCalendarOpen(false)
                      setTimePickerOpen(s => !s)
                    }}

                    role="button"
                    aria-haspopup="dialog"
                    aria-expanded={timePickerOpen}
                  >
                    <motion.div
                      className="clock-ico-container"
                      initial={{ opacity: 0, x: 8, scale: 0.96, filter: "blur(2px)" }}
                      animate={{ opacity: 1, x: 0, scale: 1, filter: "blur(0px)" }}
                      exit={{ opacity: 0, x: 8, scale: 0.96, filter: "blur(2px)" }}
                      transition={{ delay: 0.15, duration: 0.2, ease: [0.25, 0.8, 0.3, 1] }}
                    >
                      <Clock className="chip-ico clock-ico" />
                    </motion.div>
                    <AnimatePresence mode="wait" initial={false}>
                      {selectedTime && (
                        <motion.span
                          key={selectedTime.format("HH:mm")}
                          className="chip-time-label"
                          initial={{ opacity: 0, y: 6, scale: 0.92 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: -6, scale: 0.92 }}
                          transition={{ delay: 0.1, duration: 0.22, ease: [0.25, 0.8, 0.3, 1] }}
                        >
                          {selectedTime.format("h:mm A")}
                        </motion.span>
                      )}
                    </AnimatePresence>


                    <AnimatePresence>
                      {timePickerOpen && (
                        <motion.div
                          key="custom-time-picker"
                          className="time-picker-dropdown"
                          initial={{ opacity: 0, y: 8, scale: 0.96 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: 8, scale: 0.96, }}
                          transition={{ delay: 0.1, duration: 0.3, ease: [0.25, 0.8, 0.3, 1] }}
                          style={{
                            position: "absolute",
                            top: timePickerAnchorRef.current?.offsetTop + 42 || 0,
                            right: -1.5,
                            zIndex: 3100,
                          }}
                          onPointerDown={(e) => e.stopPropagation()}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {Array.from({ length: 48 }, (_, i) => {
                            const totalMinutes = i * 30;
                            const hour24 = Math.floor(totalMinutes / 60);
                            const minute = totalMinutes % 60;

                            const label = new Date(0, 0, 0, hour24, minute).toLocaleTimeString([], {
                              hour: "numeric",
                              minute: "2-digit",
                              hour12: true,
                            });

                            const isSelected =
                              selectedTime &&
                              selectedTime.hour() === hour24 &&
                              selectedTime.minute() === minute;

                            let isPastForToday = false;
                            if (isInputDateToday) {
                              const candidate = new Date(selectedDate);
                              candidate.setHours(hour24, minute, 0, 0);
                              isPastForToday = candidate.getTime() < now.getTime();
                            }

                            return (
                              <div
                                key={`${hour24}-${minute}`}
                                className={
                                  `time-option` +
                                  (isSelected ? " selected" : "") +
                                  (isPastForToday ? " past" : "")
                                }
                                onClick={() => {
                                  if (isPastForToday) return;

                                  const newTime = dayjs()
                                    .hour(hour24)
                                    .minute(minute)
                                    .second(0);

                                  setSelectedTime(newTime);
                                  setManualTimeSet(true);
                                  setTimePickerOpen(false);
                                }}
                              >
                                <span>{label}</span>
                                {isSelected && <CheckMark className="time-check" />}
                              </div>
                            );
                          })}
                        </motion.div>
                      )}

                    </AnimatePresence>
                  </motion.div>


                  <motion.div
                    key="date"
                    layout
                    className="task-date-chip"
                    initial={{ opacity: 0, y: 6, x: 4 }}
                    animate={{ opacity: 1, y: 0, x: 0 }}
                    exit={{ opacity: 0, y: -6, x: 4, }}
                    transition={{ type: 'spring', stiffness: 520, damping: 36, mass: 0.7 }}
                    onClick={e => {
                      e.stopPropagation()
                      if (timePickerOpen) setTimePickerOpen(false)
                      setCalendarOpen(s => !s)
                    }}

                    ref={calendarAnchorRef}
                    onPointerDownCapture={(e) => { e.stopPropagation(); }}
                    role="button"
                    aria-haspopup="dialog"
                  >
                    <motion.div
                      initial={{ opacity: 0, x: 8, scale: 0.96, filter: "blur(2px)" }}
                      animate={{ opacity: 1, x: 0, scale: 1, filter: "blur(0px)" }}
                      exit={{ opacity: 0, x: 8, scale: 0.96, filter: "blur(2px)" }}
                      transition={{ delay: 0.15, duration: 0.2, ease: [0.25, 0.8, 0.3, 1] }}
                    >
                      <Calendar className="chip-ico calendar-ico" />
                    </motion.div>
                    <AnimatePresence mode="wait" initial={false}>
                      <motion.div
                        key={selectedDate ? selectedDate.toISOString().slice(0, 10) : "none"}
                        className="task-date-chip-text"
                        initial={{ opacity: 0, y: 6, scale: 0.92 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -6, scale: 0.92 }}
                        transition={{ duration: 0.22, ease: [0.25, 0.8, 0.3, 1] }}
                      >
                        {selectedDate
                          ? selectedDate.toLocaleDateString("en-US", { day: "numeric", month: "short" })
                          : dateLabel}
                      </motion.div>
                    </AnimatePresence>

                  </motion.div>

                  <AnimatePresence>
                    {calendarOpen && (
                      <motion.div
                        key="calendar-popup"
                        ref={calendarPopRef}
                        initial={{ opacity: 0, y: 8, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 8, scale: 0.95 }}
                        transition={{ duration: 0.3, ease: [0.25, 0.8, 0.3, 1] }}
                        className="calendar-popup"
                        style={{
                          position: "absolute",
                          top: calendarAnchorRef.current?.offsetTop + 42 || 0,
                          right: -1.5,
                          zIndex: 3000,
                          transformOrigin: "top right",
                          willChange: "transform, opacity"
                        }}
                        onClick={e => e.stopPropagation()}
                        layout
                      >
                        {(() => {
                          const monthStart = startOfMonth(calendarMonth);
                          const monthEnd = endOfMonth(calendarMonth);
                          const startDate = startOfWeek(monthStart);
                          const endDate = endOfWeek(monthEnd);

                          const rows = [];
                          let days = [];
                          let day = startDate;

                          while (day <= endDate) {
                            for (let i = 0; i < 7; i++) {
                              const clone = day;
                              const isPast = day < new Date().setHours(0, 0, 0, 0);
                              days.push(


                                <motion.button
                                  key={day.getTime()}
                                  variants={cellV}
                                  whileHover={!isPast ? { scale: 1.04 } : {}}
                                  whileTap={!isPast ? { scale: 0.96 } : {}}
                                  disabled={isPast}
                                  className={
                                    "calendar-day" +
                                    (!isSameMonth(day, calendarMonth) ? " dim" : "") +
                                    (isPast ? " disabled" : "") +
                                    (isSameDay(day, selectedDate) ? " selected" : "")
                                  }
                                  onClick={() => {
                                    if (isPast) return;
                                    setTimeout(() => {
                                      setSelectedDate(clone);
                                      setManualDateSet(true);
                                      setCalendarOpen(false);
                                    }, 40);

                                  }}
                                >
                                  {format(day, "d")}
                                </motion.button>

                              );
                              day = addDays(day, 1);
                            }
                            rows.push(<div className="calendar-row" key={day.getTime()}>{days}</div>);
                            days = [];
                          }

                          return (
                            <div className="calendar-box">
                              <div className="calendar-head">
                                <button className="calendar-arrow-container" onClick={() => setCalendarMonth(subMonths(calendarMonth, 1))}><ArrowLeft className="calendar-arrow" /></button>
                                <span>{format(calendarMonth, "MMMM yyyy")}</span>
                                <button className="calendar-arrow-container" onClick={() => setCalendarMonth(addMonths(calendarMonth, 1))}><ArrowRight className="calendar-arrow" /></button>
                              </div>

                              <motion.div
                                key={calendarMonth.getTime()}
                                variants={gridV}
                                initial="hidden"
                                animate="show"
                                className="calendar-body"
                              >
                                {rows}
                              </motion.div>
                            </div>
                          );
                        })()}
                      </motion.div>
                    )}
                  </AnimatePresence>

                </div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>


        <MotionConfig transition={{ layout: isDragging ? { duration: 0 } : { type: "spring", stiffness: 600, damping: 50 } }}>

          <LayoutGroup id="lists" layout>
            <DndContext
              collisionDetection={collisionDetection}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              sensors={sensors}
            >



              <DroppableContainer id="tasks">

                <SortableContext
                  items={tasks.map((t) => t.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="task-filters">
                    <div className="filter-options-row">
                      <motion.button
                        className="filter-option"
                        data-tip="All tasks (1)"
                        onClick={() => setActiveFilter("all")}
                      >
                        {activeFilter === "all" && (
                          <motion.div className="filter-selection-bg" layoutId="filter-selection" transition={{ type: "spring", bounce: 0.25, duration: 0.6 }} />
                        )}
                        <span className={`filter-option-content ${activeFilter === "all" ? "selected" : ""}`}>
                          All
                        </span>
                        <span className="filter-tooltip">1</span>
                      </motion.button>

                      <motion.button
                        className="filter-option"
                        onClick={() => setActiveFilter("today")}
                      >
                        {activeFilter === "today" && (
                          <motion.div className="filter-selection-bg" layoutId="filter-selection" transition={{ type: "spring", bounce: 0.25, duration: 0.6 }} />
                        )}
                        <span className={`filter-option-content ${activeFilter === "today" ? "selected" : ""}`}>
                          Today
                        </span>
                        <span className="filter-tooltip">2</span>
                      </motion.button>

                      <motion.button
                        className="filter-option"
                        onClick={() => setActiveFilter("upcoming")}
                      >
                        {activeFilter === "upcoming" && (
                          <motion.div className="filter-selection-bg" layoutId="filter-selection" transition={{ type: "spring", bounce: 0.25, duration: 0.6 }} />
                        )}
                        <span className={`filter-option-content ${activeFilter === "upcoming" ? "selected" : ""}`}>
                          Upcoming
                        </span>
                        <span className="filter-tooltip">3</span>
                      </motion.button>

                      <motion.button
                        className="filter-option"
                        onClick={() => setActiveFilter("overdue")}
                      >
                        {activeFilter === "overdue" && (
                          <motion.div className="filter-selection-bg" layoutId="filter-selection" transition={{ type: "spring", bounce: 0.25, duration: 0.5 }} />
                        )}
                        <span className={`filter-option-content ${activeFilter === "overdue" ? "selected" : ""}`}>
                          Overdue
                        </span>
                        <span className="filter-tooltip">4</span>
                      </motion.button>
                    </div>
                    <div className="task-search-wrap">
                      <Search className="task-search-icon" aria-hidden="true" />
                      <motion.input
                        onKeyDownCapture={(e) => {
                          if (e.key !== "Escape") return;
                          setSearchQuery("")

                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        ref={searchInputRef}
                        className="task-search-input"
                        placeholder="Search by task name..."
                        value={searchQuery}
                        onPointerDownCapture={(e) => e.stopPropagation()}
                        onFocus={() => { keepSearchFocusRef.current = true; }}
                        onBlur={() => { }}
                        onChange={(e) => {
                          setSearchQuery(e.target.value);

                          requestAnimationFrame(() => {
                            if (!keepSearchFocusRef.current) return;
                            const el = searchInputRef.current;
                            if (!el) return;
                            el.focus({ preventScroll: true });
                            const len = el.value.length;
                            try { el.setSelectionRange(len, len); } catch { }
                          });
                        }}

                      />

                      {!!searchQuery.trim() && (
                        <button
                          type="button"
                          className="task-search-clear"
                          aria-label="Clear search"
                          onPointerDownCapture={(e) => { e.preventDefault(); e.stopPropagation(); }} // keep focus
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setSearchQuery("");
                            keepSearchFocusRef.current = true;
                            requestAnimationFrame(() => searchInputRef.current?.focus({ preventScroll: true }));
                          }}
                        >
                          <DeleteQuery className="task-search-clear-icon" />
                        </button>
                      )}
                    </div>
                  </div>


                  <div className="tasks-list-container">
                    {searchedTasks.length === 0 ? (
                      <div
                        className="empty-state"
                      >
                        <Logo className="empty-state-logo" />
                        <p className="empty-state-text">
                          {searchQuery
                            ? `No results for “${searchQuery}”`
                            : activeFilter === "all"
                              ? "No tasks remaining"
                              : `No ${activeFilter} tasks`}
                        </p>


                      </div>
                    ) : (
                      <AnimatePresence initial={false}>
                        {searchedTasks.map(task => {
                          const { time, date } = formatTaskDate(task.due.parsedDate);
                          const dateClass = getTaskDateClass(task);
                          const timeClass = getTaskTimeClass(task);
                          const isGroupDragging = isDragging && multiDragging.includes(task.id);
                          const isBaseHidden = hiddenIds.includes(task.id);
                          const draggedCount = multiDragging.length || 1;

                          return (
                            <SortableTaskItem
                              isHidden={searchSet && !searchSet.has(task.id)}
                              key={task.id}
                              task={task}
                              isSelected={selectedIdSet.has(task.id)}
                              time={time}
                              date={date}
                              dateClass={dateClass}
                              timeClass={timeClass}
                              onMouseEnter={() => !isDragging && setBgMood(moodFromDateClass(dateClass, task))}
                              onMouseLeave={() => setBgMood(null)}
                              onCheck={() => handleCheck(task, false)}
                              onDelete={(id) => deleteTaskWithAnimation(id, false)}
                              onDuplicate={(id) => duplicateTask(id, false)}
                              onTitleCommit={(title) => handleTitleCommit(task.id, title, false)}
                              onDateChange={(newDate) => handleTaskDateChange(task.id, false, newDate)}
                              onTimeChange={(newDate) => handleTaskTimeChange(task.id, false, newDate)}
                              isDraggingGlobal={isDragging}
                              isDeleting={deletingIds.includes(task.id)}
                              isGroupDragging={isGroupDragging}
                              isBaseHidden={isBaseHidden}
                              draggedCount={draggedCount}
                            />
                          );
                        })}


                      </AnimatePresence>
                    )}

                  </div>
                </SortableContext>
              </DroppableContainer>


              <div className="completed-tasks-list" ref={completedRef}>
                <div className="completed-tasks-header">
                  <div
                    className={`completed-tasks-line ${isCompletedTasksOpen
                      ? "line-expand"
                      : "line-hidden"
                      }`}
                  ></div>
                  <p className="completed-tasks-title" onClick={toggleCompletedTasks}>
                    Completed ({completedTasks.length})
                  </p>
                  <div
                    className={`completed-tasks-line ${isCompletedTasksOpen
                      ? "line-expand"
                      : "line-hidden"
                      }`}
                  ></div>
                </div>
                <AnimatePresence>
                  {isCompletedTasksOpen && (
                    <motion.div
                      style={{ width: "100%" }}
                      initial={{ opacity: 0, y: 8, scale: 0.995, filter: "blur(8px)" }}
                      animate={{ opacity: 1, y: 0, scale: 1, filter: "blur(0px)" }}
                      exit={{ opacity: 0, y: 8, scale: 0.995, filter: "blur(8px)" }}
                      transition={{ duration: 0.6, ease: [0.25, 0.8, 0.3, 1] }}
                    >
                      {completedTasks.length === 0 ? (
                        <motion.div initial={{ scale: 0.995, opacity: 0, filter: "blur(8px)" }} animate={{ scale: 1, opacity: 1, filter: "blur(0px)" }} exit={{ scale: 0.995, opacity: 0, filter: "blur(8px)" }} transition={{ duration: 0.5, ease: [0.25, 0.8, 0.3, 1] }} className="empty-state">
                          <Logo className="empty-state-logo" />
                          <p className="empty-state-text">No completed tasks</p>
                        </motion.div>
                      ) : (
                        <DroppableContainer id="completedTasks">
                          <SortableContext
                            items={completedTasks.map((t) => t.id)}
                            strategy={verticalListSortingStrategy}
                          >
                            <div className="tasks-list-container">
                              {completedTasks.map(task => {
                                const { time, date } = formatTaskDate(task.due.parsedDate);
                                const isGroupDragging = isDragging && multiDragging.includes(task.id);
                                const isBaseHidden = hiddenIds.includes(task.id);
                                const draggedCount = multiDragging.length || 1;

                                return (
                                  <SortableTaskItem
                                    key={task.id}
                                    task={task}
                                    isSelected={selectedIdSet.has(task.id)}
                                    time={time}
                                    date={date}
                                    dateClass={getTaskDateClass(task)}
                                    timeClass={getTaskTimeClass(task)}
                                    onCheck={() => handleCheck(task, true)}
                                    onDelete={(id) => deleteTaskWithAnimation(id, true)}
                                    onDuplicate={(id) => duplicateTask(id, true)}
                                    onTitleCommit={(title) => handleTitleCommit(task.id, title, true)}
                                    onDateChange={(newDate) => handleTaskDateChange(task.id, true, newDate)}
                                    onTimeChange={(newDate) => handleTaskTimeChange(task.id, true, newDate)}
                                    isDraggingGlobal={isDragging}
                                    isDeleting={deletingIds.includes(task.id)}
                                    isGroupDragging={isGroupDragging}
                                    isBaseHidden={isBaseHidden}
                                    draggedCount={draggedCount}
                                  />
                                );
                              })}



                            </div>
                          </SortableContext>
                        </DroppableContainer>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>


                <DragOverlay >
                  {multiDragging.length > 1 && activeId ? (
                    <div style={{ position: "relative" }}>
                      {multiDragging.map(id => {
                        const taskObj =
                          tasks.find(t => t.id === id) ||
                          completedTasks.find(t => t.id === id);
                        if (!taskObj) return null;

                        const { x = 0, y = 0 } = multiOffsets[id] || {};
                        const { time, date } = formatTaskDate(taskObj.due?.parsedDate);
                        const dateClass = getTaskDateClass(taskObj);
                        const timeClass = getTaskTimeClass(taskObj);

                        return (
                          <div
                            key={id}
                            style={{
                              position: "absolute",
                              transform: `translate(${x}px, ${y}px)`,
                              width: "100%",
                            }}
                          >
                            <SortableTaskItem
                              task={taskObj}
                              time={time}
                              date={date}
                              dateClass={dateClass}
                              timeClass={timeClass}
                              onCheck={() => { }}
                              isOverlay
                            />
                          </div>
                        );
                      })}
                    </div>
                  ) : activeId ? (
                    (() => {
                      const activeTask =
                        tasks.find(t => t.id === activeId) ||
                        completedTasks.find(t => t.id === activeId);
                      if (!activeTask) return null;
                      const { time, date } = formatTaskDate(activeTask.due?.parsedDate);
                      const dateClass = getTaskDateClass(activeTask);
                      const timeClass = getTaskTimeClass(activeTask);
                      return (
                        <SortableTaskItem
                          task={activeTask}
                          time={time}
                          date={date}
                          dateClass={dateClass}
                          timeClass={timeClass}
                          onCheck={() => { }}
                          isOverlay
                        />
                      );
                    })()
                  ) : null}
                </DragOverlay>



              </div>
            </DndContext>
          </LayoutGroup>
        </MotionConfig>
      </div >
      {!isDragging && (
        <Selecto
          key="selecto"
          ref={selectoRef}
          container={document.body}
          selectableTargets={[".selectable"]}
          selectByClick={false}
          selectFromInside={false}
          continueSelect={false}
          onDrag={e => {
            const box = document.querySelector(".selecto-area");
            if (box) {
              box.style.setProperty("--x", e.clientX + "px");
              box.style.setProperty("--y", e.clientY + "px");
            }
          }}


          hitRate={0}
          ratio={0}

          onSelect={e => {
            setSelectedTaskIds(
              e.selected
                .map((el) => el.getAttribute("data-id"))
                .filter(Boolean)
            );
          }}
          onSelectEnd={e => {
          }}
        />
      )}

      {createPortal(
        <AnimatePresence>
          {!isDragging && selectedTaskIds.length > 0 && (
            <motion.div
              className="bulk-actions-bar"
              initial={{ y: 80, opacity: 0, scale: 0.98, x: "-50%", filter: "blur(10px)" }}
              animate={{ y: 0, opacity: 1, scale: 1, x: "-50%", filter: "blur(0px)" }}
              exit={{ y: 80, opacity: 0, scale: 0.98, x: "-50%", filter: "blur(10px)" }}
              transition={{ duration: 0.24, ease: EASE_SOFT }}
            >
              <div className="bulk-pill">
                <div className="bulk-selected-cell">
                  <span className="bulk-selected-count">{selectedTaskIds.length}</span>
                  <span className="bulk-selected-label">Selected</span>
                </div>

                <div className="bulk-divider" />

                <motion.button
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 15 }}
                  transition={{ delay: 0.07, duration: 0.05, transition: "ease" }}
                  type="button"
                  className="bulk-chip"
                  onClick={bulkMarkDone}
                  disabled={!selectedActiveCount}
                >
                  <span className="bulk-chip-count">{selectedActiveCount}</span>
                  <span className="bulk-tooltip">Mark as done (D)</span>
                  <CheckMark className="bulk-chip-icon" aria-hidden="true" />

                </motion.button>

                <motion.button
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 15 }}
                  transition={{ delay: 0.08, duration: 0.05, transition: "ease" }}
                  type="button"
                  className="bulk-chip"
                  onClick={bulkMarkUndone}
                  disabled={!selectedCompletedCount}
                >
                  <span className="bulk-chip-count">{selectedCompletedCount}</span>
                  <span className="bulk-tooltip">Mark as undone (G)</span>
                  <Undo className="bulk-chip-icon" aria-hidden="true" />

                </motion.button>

                <motion.button
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 15 }}
                  transition={{ delay: 0.09, duration: 0.05, transition: "ease" }}
                  type="button"
                  className="bulk-chip danger"
                  onClick={bulkDelete}
                >
                  <span className="bulk-tooltip">Delete (X)</span>
                  <Delete className="bulk-icon" aria-hidden="true" />
                </motion.button>

                <motion.button
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 15 }}
                  transition={{ delay: 0.1, duration: 0.05, transition: "ease" }}
                  type="button"
                  className="bulk-chip ghost"
                  onClick={clearSelection}
                >
                  <span className="bulk-tooltip">Unselect (ESC)</span>
                  <Close className="bulk-icon" aria-hidden="true" />

                </motion.button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}

      <AnimatePresence>
        {showScrollBtn && (
          <motion.div
            className="scroll-to-completed"
            onClick={() =>
              completedRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
            }
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.3 }}
          >
            <ArrowDown className="scroll-icon" />
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {importDialogOpen && pendingImport && (
          <motion.div
            className="import-modal-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: EASE_SOFT }}
            onClick={cancelImport}
          >
            <motion.div
              className="import-modal"
              initial={{ opacity: 0, y: 20, scale: 0.96, filter: "blur(16px)" }}
              animate={{ opacity: 1, y: 0, scale: 1, filter: "blur(0px)" }}
              exit={{ opacity: 0, y: 10, scale: 0.96, filter: "blur(16px)" }}
              transition={{ duration: 0.35, ease: EASE_SOFT }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="import-modal-header">
                <p className="import-modal-title">Import tasks</p>
                <p className="import-modal-subtitle">How do you want to apply the imported tasks?</p>
              </div>

              <div className="import-modal-summary">
                <span>{pendingImport.tasks.length} active</span>
                <span>·</span>
                <span>{pendingImport.completedTasks.length} completed</span>
              </div>

              <div className="import-modal-actions">
                <button
                  type="button"
                  className="import-btn ghost"
                  onClick={cancelImport}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="import-btn secondary"
                  onClick={() => applyImport("merge")}
                >
                  Merge with current
                </button>
                <button
                  type="button"
                  className="import-btn primary"
                  onClick={() => applyImport("replace")}
                >
                  Replace current
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {importToast && (
          <motion.div
            className={`import-toast ${importToast.type}`}
            initial={{ opacity: 0, y: -14, scale: 0.98, filter: "blur(8px)" }}
            animate={{ opacity: 1, y: 0, scale: 1, filter: "blur(0px)" }}
            exit={{ opacity: 0, y: -14, scale: 0.98, filter: "blur(8px)" }}
            transition={{ duration: 0.3, ease: EASE_SOFT }}
          >
            {importToast.message}
          </motion.div>
        )}
      </AnimatePresence>


      <input
        ref={fileInputRef}
        type="file"
        accept="application/json"
        style={{ display: "none" }}
        onChange={handleImportFile}
      />



    </motion.div >
  );
};

export default Tasks;
